import { toCurrentRoutePath } from './legacyRoutePrefixes';

/** Competency & Training: company-wide lists. Assessments and effectiveness stay open to line managers (server scopes them). */
const COMPETENCY_VIEW_ANY = [
  'competency.view',
  'competency.training.view',
  'competency.assessment.view',
  'competency.assessment.evaluate',
  'competency.assessment.close',
  'competency.training.manage',
  'competency.job_profile.manage',
];
const COMPETENCY_TRAINING_VIEW = ['competency.view', 'competency.training.view', 'competency.training.manage'];

/**
 * Route → permission codes (user needs ANY one). Used for sidebar, create menu, command palette.
 * Routes not listed are treated as accessible (backend still enforces APIs).
 */
export const ROUTE_REQUIRED_PERMISSIONS: Record<string, string[]> = {
  // Home
  '/home/tasks': [
    'user.view',
    'company.view',
    'growth.view',
    'procurement.view',
    'manufacturing.view',
    'finance.view',
    'inventory.view',
    'procurement.pr.view',
    'procurement.spot_purchase.view',
    'procurement.po.view',
    'growth.order.view',
    'growth.delivery.view',
  ],
  '/home/team-tasks': [
    'user.view',
    'company.view',
    'growth.view',
    'procurement.view',
    'manufacturing.view',
    'finance.view',
    'inventory.view',
    'procurement.pr.view',
    'procurement.spot_purchase.view',
    'procurement.po.view',
    'growth.order.view',
    'growth.delivery.view',
  ],
  '/home/drafts': ['user.view', 'company.view'],
  '/home/alerts': ['user.view', 'company.view'],
  // '/dashboard' (My Workboard) and '/home/approvals' intentionally ungated: personal inbox; APIs scope to the caller.
  // Company overview reuses the same openness as the former /dashboard executive collage.
  '/home/company-overview': ['user.view', 'company.view'],
  '/unified': [
    'company.view',
    'growth.view',
    'procurement.view',
    'manufacturing.view',
    'finance.view',
    'inventory.view',
    'procurement.pr.view',
    'procurement.spot_purchase.view',
    'procurement.po.view',
    'growth.order.view',
    'growth.delivery.view',
  ],
  '/home/exceptions': [
    'management.exceptions.view',
    'owner_intelligence.view',
    'company.view',
    'growth.view',
    'procurement.view',
    'finance.view',
  ],
  '/reports': [
    'finance.reports.view',
    'growth.reports.view',
    'procurement.reports.view',
    'inventory.reports.view',
    'project.reports.view',
    'manufacturing.reports.view',
    'hr.reports.view',
    'asset.reports.view',
    'asset.view',
    'finance.view',
    'growth.view',
    'procurement.view',
  ],
  '/reports/audit': [
    'finance.reports.view',
    'growth.reports.view',
    'procurement.reports.view',
    'inventory.reports.view',
    'project.reports.view',
    'manufacturing.reports.view',
    'hr.reports.view',
    'asset.reports.view',
    'company.view',
  ],

  // Sales
  '/growth/dashboard': ['growth.dashboard.view', 'growth.view'],
  '/growth/leads': ['crm.lead.view', 'growth.view'],
  '/growth/leads/new': ['crm.lead.view', 'growth.view'],
  '/growth/opportunities': ['crm.opportunity.view', 'growth.view'],
  '/growth/opportunities/new': ['crm.opportunity.view', 'growth.view'],
  '/growth/calendar': ['crm.activity.view', 'growth.view'],
  '/growth/activities': ['crm.activity.view', 'growth.view'],
  '/growth/activities/new': ['crm.activity.create'],
  '/growth/follow-ups': ['crm.activity.view', 'growth.view'],
  '/growth/standup': ['crm.standup.view', 'crm.standup.submit', 'growth.view'],
  '/growth/standup/team': ['crm.standup.manage'],
  '/growth/standup/history': ['crm.standup.view', 'crm.standup.manage', 'growth.view'],
  '/growth/setup/activity-types': ['crm.activity_type.manage'],
  '/growth/demos': ['crm.demo.view'],
  '/growth/demos/new': ['crm.demo.create'],
  '/growth/sales-targets': ['growth.sales_target.manage', 'growth.sales_target.view'],
  '/growth/reports/weekly-sales-performance': ['growth.reports.view', 'growth.view', 'growth.sales_target.view', 'growth.sales_target.manage'],
  '/growth/quotations': ['crm.quotation.view', 'growth.view'],
  '/growth/quotations/new': ['crm.quotation.create', 'growth.view'],
  '/growth/setup/quotation-defaults': ['crm.quotation_settings.manage', 'crm.quotation.view', 'growth.view'],
  '/growth/estimations': ['crm.estimation.view', 'growth.view'],
  '/growth/order-review-checklists': ['crm.order_review_checklist.view', 'growth.view'],
  '/growth/estimations/boq': ['crm.estimation.view', 'growth.view'],
  '/growth/estimations/assembly': ['growth.assembly_estimation.view'],
  '/growth/estimations/assembly/new': ['growth.assembly_estimation.create', 'growth.assembly_estimation.view'],
  '/growth/estimations/trading-rental': ['growth.trading_rental_estimation.view', 'growth.view'],
  '/growth/estimations/trading-rental/create': ['growth.trading_rental_estimation.create', 'growth.view'],
  '/growth/quotation-consolidation': [
    'growth.quotation.consolidate',
    'crm.quotation.convert',
    'growth.order.create',
    'growth.view',
  ],
  '/growth/quotation-conversion-reports': ['crm.quotation.view', 'growth.view', 'growth.reports.view'],
  '/growth/reports': ['growth.reports.view', 'growth.view'],
  '/growth/reports/invoice-register': ['growth.reports.view', 'growth.view'],
  '/growth/reports/order-register': ['growth.reports.view', 'growth.view'],
  '/growth/reports/delivery-note-register': ['growth.reports.view', 'growth.view'],
  '/growth/reports/customer-analysis': ['growth.reports.customerAnalysis', 'growth.reports.view', 'growth.view'],
  '/growth/reports/invoice-aging': ['growth.reports.invoiceAging', 'growth.reports.view', 'growth.view'],
  '/growth/reports/tre-overhead': ['growth.overhead.view', 'growth.profitability.view', 'growth.margin.view', 'growth.view'],
  '/growth/reports/project-costing': ['growth.profitability.view', 'growth.margin.view'],
  '/growth/reports/conversion-funnel': ['growth.reports.view', 'growth.view'],
  '/growth/reports/win-loss': ['growth.reports.view', 'growth.view'],
  '/growth/reports/opportunity-conversion': ['growth.reports.view', 'growth.view'],
  '/growth/reports/salesperson-performance': ['growth.reports.view', 'growth.view'],
  '/growth/reports/product-rst-mix': ['growth.reports.view', 'growth.view'],
  '/growth/reports/booking-billing-collections': ['growth.reports.view', 'growth.view'],
  '/growth/reports/quotation-aging': ['growth.reports.view', 'growth.view'],
  '/growth/reports/opportunity-aging': ['growth.reports.view', 'growth.view'],
  '/growth/reports/credit-note-register': ['growth.reports.view', 'growth.view'],
  '/growth/reports/opportunity-register': ['growth.reports.view', 'growth.view'],
  '/growth/reports/quotation-list-register': ['growth.reports.view', 'growth.view'],
  '/growth/reports/opportunity-to-fulfillment': ['growth.reports.view', 'growth.view'],
  '/growth/dashboards/team-command-center': [
    'growth.team.view_all',
    'growth.team.view_team',
    'growth.team.view_own',
    'growth.reports.view',
    'growth.view',
  ],
  '/growth/orders': ['growth.order.view', 'growth.view'],
  '/growth/order-timesheets': ['growth.order_timesheet.view', 'growth.order.view', 'growth.view'],
  '/growth/order-health': ['growth.order.view', 'growth.view'],
  '/growth/inland-process-deviations': ['growth.order.view', 'growth.view'],
  '/growth/invoices': ['growth.invoice.view', 'growth.view'],
  '/growth/delivery-notes': ['growth.delivery.view'],
  '/growth/document-collections': ['growth.document_collection.view', 'growth.view'],
  '/growth/delivery-readiness': ['growth.delivery.readiness.view', 'growth.delivery.view'],
  '/growth/my-deliveries': ['growth.delivery.complete', 'growth.delivery.view'],
  '/growth/packing-list': ['growth.delivery.view'],
  '/growth/customers': ['growth.customer.view'],
  '/growth/customer-success': ['growth.customer.view', 'growth.view'],
  '/growth/customer-satisfaction': [
    'growth.customer_satisfaction.view',
    'growth.customer.view',
    'growth.view',
  ],
  '/growth/contract-reviews': ['crm.contract_review.view', 'growth.view'],

  // Customer Property (CSPM)
  '/cspm': ['cspm.view'],
  '/cspm/requirements/new': ['cspm.requirement.create', 'cspm.view'],
  '/cspm/receipts/new': ['cspm.receipt.create', 'cspm.view'],
  '/cspm/returns/new': ['cspm.return_customer', 'cspm.view'],
  '/cspm/reconciliations': ['cspm.reconcile', 'cspm.view'],

  // SCM
  '/scm/dashboard': ['procurement.view', 'procurement.reports.view'],
  '/scm/reports': ['procurement.reports.view', 'inventory.reports.view', 'procurement.view'],
  '/scm/reports/purchase-register': ['procurement.reports.view', 'procurement.po.view', 'procurement.view'],
  '/scm/reports/grn-register': ['procurement.reports.view', 'procurement.grn.view', 'procurement.view'],
  '/scm/reports/supplier-invoice-register': ['procurement.reports.view', 'procurement.view'],
  '/scm/reports/purchase-return-register': ['procurement.reports.view', 'procurement.view'],
  '/scm/reports/purchase-requisition-register': ['procurement.reports.view', 'procurement.view'],
  '/scm/reports/po-aging': ['procurement.reports.poAging', 'procurement.reports.view', 'procurement.view'],
  '/scm/reports/supplier-analysis': ['procurement.reports.supplierAnalysis', 'procurement.reports.view', 'procurement.view'],
  '/scm/reports/service-receipt-register': ['procurement.reports.view', 'procurement.view'],
  '/scm/reports/stock-movement': ['inventory.reports.view', 'inventory.view'],
  '/scm/reports/material-request-register': ['inventory.reports.view', 'inventory.view'],
  '/scm/reports/material-issue-register': ['inventory.reports.view', 'inventory.view', 'manufacturing.reports.view'],
  '/scm/reports/inventory-valuation': ['inventory.reports.view', 'inventory.view'],
  '/scm/reports/gl-reconciliation': ['inventory.reports.view', 'inventory.view'],
  '/scm/reports/project-material-reconciliation': ['inventory.reports.view', 'inventory.view'],
  '/scm/reports/stock-aging': ['inventory.reports.view', 'inventory.view'],
  '/scm/reports/abc-analysis': ['inventory.reports.view', 'inventory.view'],
  '/scm/reports/dead-stock': ['inventory.reports.view', 'inventory.view'],
  '/scm/reports/purchase-price-variance': [
    'inventory.reports.view',
    'inventory.view',
    'procurement.reports.view',
  ],
  '/scm/reports/demand-planning': [
    'inventory.reports.view',
    'inventory.view',
    'procurement.pr.view',
    'procurement.pr.create',
  ],
  '/scm/reports/landed-cost-revalue': [
    'inventory.reports.view',
    'inventory.view',
    'inventory.adjustment.create',
    'inventory.adjustment.post',
  ],
  '/scm/reports/cspm-on-hand': ['cspm.view', 'inventory.reports.view', 'inventory.view'],
  '/scm/reports/cspm-awaiting-inspection': ['cspm.view', 'inventory.reports.view', 'inventory.view'],
  '/scm/reports/cspm-exceptions': ['cspm.view', 'inventory.reports.view', 'inventory.view'],
  '/scm/reports/cspm-quality-hold': ['cspm.view', 'inventory.reports.view', 'inventory.view'],
  '/scm/procurement/suppliers': ['procurement.supplier.view'],
  '/scm/procurement/suppliers/:id': ['procurement.supplier.view'],
  '/scm/procurement/suppliers/:id/360': ['procurement.supplier.view'],
  '/scm/procurement/control-tower': ['procurement.control_tower.view', 'procurement.reports.view'],
  '/scm/procurement/supplier-categories': ['procurement.supplier.view'],
  '/scm/procurement/purchase-requests': [
    'procurement.pr.view',
    'procurement.po.view',
    'procurement.view',
  ],
  '/scm/procurement/spot-purchase': [
    'procurement.spot_purchase.view',
    'procurement.spot_purchase.assign',
    'procurement.spot_purchase.supervise',
    'procurement.view',
  ],
  '/scm/procurement/supplier-inquiries': ['procurement.rfq.view', 'procurement.po.view', 'procurement.view'],
  '/scm/procurement/supplier-quotations': ['procurement.rfq.view', 'procurement.po.view', 'procurement.view'],
  '/scm/procurement/bid-analyses': ['procurement.rfq.view', 'procurement.po.view', 'procurement.view'],
  '/scm/procurement/purchase-orders': ['procurement.po.view', 'procurement.view'],
  '/scm/procurement/supplier-advance-requests': [
    'procurement.advance.view',
    'procurement.po.view',
    'procurement.view',
  ],
  '/scm/procurement/grn': ['procurement.grn.view', 'procurement.po.view', 'procurement.view'],
  '/scm/procurement/receiving-inspections': [
    'quality.receiving_inspection.view',
    'quality.inspection.view',
    'manufacturing.view',
  ],
  '/scm/procurement/receiving-inspections/:id': [
    'quality.receiving_inspection.view',
    'quality.inspection.view',
    'manufacturing.view',
  ],
  '/scm/procurement/receiving-dimensional-inspections': [
    'quality.receiving_dimensional_inspection.view',
    'quality.receiving_inspection.view',
    'quality.inspection.view',
    'manufacturing.view',
  ],
  '/scm/procurement/receiving-dimensional-inspections/:id': [
    'quality.receiving_dimensional_inspection.view',
    'quality.receiving_inspection.view',
    'quality.inspection.view',
    'manufacturing.view',
  ],
  '/scm/procurement/service-receipts': ['procurement.grn.view', 'procurement.po.view', 'procurement.view'],
  '/scm/procurement/service-receipts/:id': ['procurement.grn.view', 'procurement.po.view', 'procurement.view'],
  '/scm/procurement/reports/po-milestones': ['procurement.reports.view', 'procurement.po.view', 'procurement.view'],
  '/scm/procurement/supplier-invoices': ['procurement.invoice.view'],
  '/scm/procurement/three-way-matching': ['procurement.invoice.view', 'procurement.invoice.match'],
  '/scm/procurement/purchase-returns': ['procurement.po.view'],
  '/scm/warehouse/warehouses': ['inventory.view'],
  '/scm/warehouse/warehouses/:id': [
    'inventory.warehouse_location.view',
    'inventory.warehouse_location.manage',
    'inventory.view',
  ],
  '/scm/warehouse/stock-transfer': ['inventory.transfer.view', 'inventory.transfer.create', 'inventory.view'],
  '/scm/warehouse/stock-transfer/new': ['inventory.transfer.create'],
  '/scm/warehouse/stock-adjustment': ['inventory.adjustment.create', 'inventory.view'],
  '/scm/inventory/opening-stock': [
    'inventory.opening.upload',
    'inventory.opening.post',
    'inventory.view',
  ],
  '/scm/inventory/items': ['inventory.view'],
  '/scm/inventory/items/:id': ['inventory.view'],
  '/scm/inventory/items/new': ['inventory.view'],
  '/scm/inventory/items/:id/edit': ['inventory.view'],
  '/platform/foundation/items': ['inventory.view'],
  '/platform/foundation/items/:id': ['inventory.view'],
  '/platform/foundation/items/new': ['inventory.view'],
  '/platform/foundation/items/:id/edit': ['inventory.view'],
  '/scm/inventory/item-categories': ['inventory.view'],
  '/scm/inventory/uom-units': ['inventory.view'],
  '/scm/inventory/engineering': ['inventory.engineering.view', 'inventory.engineering.manage', 'inventory.engineering.approve'],
  '/inventory/item-classification-gl-accounts': ['finance.coa.manage', 'inventory.view'],
  '/scm/inventory/material-requests': ['inventory.material_request.view', 'inventory.view'],
  '/scm/inventory/material-issues': ['inventory.material_issue.view', 'inventory.view'],
  '/scm/inventory/material-returns': ['inventory.material_return.view', 'inventory.material_issue.view'],
  '/scm/inventory/site-materials': [
    'inventory.view',
    'inventory.transfer.view',
    'inventory.material_issue.view',
  ],
  '/scm/inventory/stock-summary': ['inventory.view', 'inventory.reports.view'],
  '/scm/inventory/heat-stock': ['inventory.view', 'inventory.reports.view'],
  '/scm/inventory/stock-count': [
    'inventory.count.view',
    'inventory.count.create',
    'inventory.view',
  ],
  '/scm/inventory/putaway': ['inventory.putaway.post', 'inventory.edit', 'inventory.view'],
  '/scm/inventory/consignment-receipts': [
    'inventory.consignment.view',
    'inventory.consignment.create',
    'inventory.view',
  ],
  '/scm/inventory/consignment-receipts/new': [
    'inventory.consignment.create',
    'inventory.edit',
  ],
  '/scm/inventory/consignment-returns': [
    'inventory.consignment.view',
    'inventory.consignment.return',
    'inventory.view',
  ],
  '/scm/inventory/consignment-returns/new': [
    'inventory.consignment.return',
    'inventory.consignment.create',
    'inventory.edit',
  ],
  '/scm/quality/criticality-risk': [
    'procurement.supplier.classify',
    'procurement.assessment.view',
    'procurement.supplier.view',
  ],
  '/scm/quality/approved-supplier-list': ['procurement.asl.view', 'procurement.supplier.view'],
  '/scm/quality/supplier-assessments': ['procurement.assessment.view', 'procurement.supplier.view'],
  '/scm/quality/supplier-assessments/new': [
    'procurement.assessment.create',
    'procurement.assessment.view',
  ],
  '/scm/quality/supplier-assessments/:id': ['procurement.assessment.view', 'procurement.supplier.view'],
  '/scm/quality/performance-monitoring': ['procurement.performance.view', 'procurement.supplier.view'],
  '/scm/quality/receiving-verification': ['procurement.grn.view', 'procurement.po.view', 'procurement.view'],
  '/scm/quality/critical-mtc': ['procurement.grn.view', 'procurement.po.view', 'procurement.view'],
  '/scm/quality/outsourcing': ['manufacturing.view', 'procurement.po.view'],

  // Asset Management
  '/asset/assets': ['asset.view'],
  '/asset/assets/new': ['asset.create'],
  '/asset/assets/:id': ['asset.view'],
  '/asset/assets/:id/edit': ['asset.edit', 'asset.view'],
  '/asset/scan': ['asset.view'],
  '/asset/maintenance': ['asset.maintenance.view', 'asset.view'],
  '/asset/workshop': ['workshop.view', 'asset.maintenance.view', 'asset.view'],
  '/asset/workshop/jobs/:caseId': ['workshop.view', 'asset.maintenance.view', 'asset.view'],
  '/asset/maintenance/cases/new': ['asset.maintenance.edit', 'asset.edit'],
  '/asset/maintenance/cases/:id': ['asset.maintenance.view', 'asset.view'],
  '/asset/maintenance/pm-plans/new': ['asset.maintenance.edit', 'asset.edit'],
  '/asset/maintenance/pm-plans/:id': ['asset.maintenance.view', 'asset.view'],
  '/asset/maintenance/pm-plans/:id/complete': ['asset.maintenance.edit', 'asset.edit'],
  '/asset/maintenance/amc/new': ['asset.maintenance.edit', 'asset.edit'],
  '/asset/maintenance/amc/:id': ['asset.maintenance.view', 'asset.view'],
  '/asset/depreciation': ['asset.depreciation.view', 'asset.view'],
  '/asset/disposal': ['asset.disposal.view', 'asset.view'],
  '/asset/accounting-defaults': ['asset.accounting.view', 'asset.view', 'asset.edit'],
  '/asset/groups': ['asset.groups.view', 'asset.view', 'asset.edit'],
  '/asset/insurance': ['asset.insurance.view', 'asset.view'],
  '/asset/insurance/policies/new': ['asset.insurance.edit', 'asset.edit'],
  '/asset/insurance/policies/:id': ['asset.insurance.view', 'asset.view'],
  '/asset/insurance/claims/new': ['asset.insurance.edit', 'asset.edit'],
  '/asset/insurance/claims/:id': ['asset.insurance.view', 'asset.view'],
  '/asset/audit': ['asset.audit.view', 'asset.view'],
  '/asset/audit/cycles/new': ['asset.audit.edit', 'asset.edit'],
  '/asset/audit/cycles/:id': ['asset.audit.view', 'asset.view'],
  '/asset/audit/cycles/:id/edit': ['asset.audit.edit', 'asset.edit'],
  '/asset/audit/cycles/:cycleId/results/:resultId': ['asset.audit.edit', 'asset.edit'],
  '/asset/assets/:id/movements/:type': ['asset.edit', 'asset.view'],
  '/asset/assets/:id/pmv/activate': ['pmv.manage', 'asset.edit'],
  '/asset/audit/report': ['asset.audit.view', 'asset.view'],
  '/asset/reports': ['asset.view'],
  '/asset/reports/movements': ['asset.view'],
  '/asset/reports/location': ['asset.view'],

  '/asset/pmv/hub': ['pmv.view', 'asset.view'],
  '/asset/pmv/control-tower': ['pmv.control_tower.view', 'pmv.view', 'asset.view'],
  '/asset/pmv/connected': ['pmv.connected.view', 'pmv.view', 'asset.view'],
  '/asset/pmv/connected/map': [
    'pmv.connected.map.view',
    'pmv.connected.geofence.view',
    'pmv.connected.view',
    'pmv.view',
  ],
  '/asset/pmv/connected/movement-attention': [
    'pmv.connected.spatial_attention.view',
    'pmv.connected.reconciliation.view',
    'pmv.connected.view',
    'pmv.view',
  ],
  '/asset/pmv/connected/geofences': [
    'pmv.connected.geofence.view',
    'pmv.connected.view',
    'pmv.view',
  ],
  '/asset/pmv/connected/geofences/new': [
    'pmv.connected.geofence.manage',
    'pmv.admin',
    'pmv.manage',
  ],
  '/asset/pmv/connected/geofences/:id': [
    'pmv.connected.geofence.view',
    'pmv.connected.geofence.manage',
    'pmv.connected.view',
    'pmv.view',
  ],
  '/asset/pmv/connected/devices': ['pmv.connected.device.view', 'pmv.connected.view', 'pmv.view'],
  '/asset/pmv/connected/providers': ['pmv.connected.provider.view', 'pmv.connected.view', 'pmv.view'],
  '/asset/pmv/connected/reconciliation': [
    'pmv.connected.reconciliation.view',
    'pmv.connected.view',
    'pmv.view',
  ],
  '/asset/pmv/connected/diagnostics': ['pmv.connected.diagnostics', 'pmv.admin', 'pmv.view'],
  '/asset/pmv': ['pmv.view', 'asset.view'],
  '/asset/pmv/components': ['pmv.component.view', 'pmv.view', 'pmv.admin', 'pmv.manage'],
  '/asset/pmv/components/control-tower': [
    'pmv.component.view',
    'pmv.control_tower.view',
    'pmv.view',
    'pmv.admin',
  ],
  '/asset/pmv/components/import': ['pmv.component.manage', 'pmv.manage', 'pmv.admin'],
  '/asset/pmv/components/:id': ['pmv.component.view', 'pmv.view', 'pmv.admin', 'pmv.manage'],
  '/asset/pmv/fuel': ['pmv.fuel.view', 'pmv.view', 'pmv.admin', 'pmv.fuel'],
  '/asset/pmv/fuel/control-tower': [
    'pmv.fuel.view',
    'pmv.control_tower.view',
    'pmv.view',
    'pmv.admin',
    'pmv.fuel',
  ],
  '/asset/pmv/fuel/reconciliation': ['pmv.fuel.view', 'pmv.fuel.reconcile', 'pmv.view', 'pmv.admin'],
  '/asset/pmv/fuel/attention': ['pmv.fuel.view', 'pmv.view', 'pmv.admin', 'pmv.fuel'],
  '/asset/pmv/fuel/sources': ['pmv.fuel.view', 'pmv.fuel.source.manage', 'pmv.view', 'pmv.admin'],
  '/asset/pmv/reliability': [
    'pmv.reliability.view',
    'pmv.view',
    'pmv.admin',
    'pmv.manage',
  ],
  '/asset/pmv/reliability/control-tower': [
    'pmv.reliability.view',
    'pmv.view',
    'pmv.admin',
    'pmv.manage',
  ],
  '/asset/pmv/reliability/attention': [
    'pmv.reliability.view',
    'pmv.reliability.manage',
    'pmv.view',
    'pmv.admin',
  ],
  '/asset/pmv/reliability/failures': ['pmv.reliability.view', 'pmv.view', 'pmv.admin'],
  '/asset/pmv/reliability/faults': ['pmv.reliability.view', 'pmv.view', 'pmv.admin'],
  '/asset/pmv/reliability/predictions': [
    'pmv.reliability.prediction.view',
    'pmv.reliability.view',
    'pmv.view',
    'pmv.admin',
  ],
  '/asset/pmv/reliability/forecast': ['pmv.reliability.view', 'pmv.view', 'pmv.admin'],
  '/asset/pmv/reliability/conditions': [
    'pmv.reliability.condition.view',
    'pmv.reliability.view',
    'pmv.view',
    'pmv.admin',
  ],
  '/asset/pmv/:id': ['pmv.view', 'asset.view'],
  '/asset/pmv/:id/tyres': ['pmv.component.view', 'pmv.view', 'pmv.admin', 'pmv.manage'],
  '/asset/pmv/:id/batteries': ['pmv.component.view', 'pmv.view', 'pmv.admin', 'pmv.manage'],
  '/asset/pmv/:id/inspections/new': ['pmv.inspection.record', 'pmv.manage', 'pmv.view'],
  '/asset/pmv/:id/breakdowns/new': ['pmv.breakdown.report', 'pmv.manage', 'pmv.view'],
  '/asset/pmv/:id/fuel/new': ['pmv.fuel.record', 'pmv.manage', 'pmv.view'],
  '/asset/pmv/:id/operators/assign': ['pmv.operator.assign', 'pmv.manage', 'pmv.view'],
  '/asset/pmv/:id/status': ['pmv.status.manage', 'pmv.manage', 'pmv.view'],
  '/asset/pmv/:id/specifications/edit': ['pmv.specification.manage', 'pmv.manage', 'pmv.view'],
  '/asset/pmv/setup': ['pmv.admin', 'pmv.classification.manage', 'pmv.view'],
  '/asset/pmv/setup/classifications/new': ['pmv.classification.manage', 'pmv.admin', 'pmv.view'],
  '/asset/pmv/setup/classifications/:id': ['pmv.classification.manage', 'pmv.admin', 'pmv.view'],
  '/asset/pmv/setup/inspection-templates/new': ['pmv.inspection.template.manage', 'pmv.admin', 'pmv.view'],
  '/asset/pmv/setup/document-requirements/new': ['pmv.admin', 'pmv.classification.manage', 'pmv.view'],
  '/asset/pmv/setup/hire-rates/new': ['pmv.cost.rate.manage', 'pmv.admin', 'pmv.view'],
  '/asset/pmv/setup/hire-rates/:id': ['pmv.cost.rate.manage', 'pmv.admin', 'pmv.view'],
  '/asset/pmv/operations': ['pmv.view', 'asset.view'],
  '/asset/pmv/requests': ['pmv.request.view', 'pmv.view'],
  '/asset/pmv/requests/new': ['pmv.request.manage', 'pmv.manage'],
  '/asset/pmv/requests/:id/edit': ['pmv.request.manage', 'pmv.manage'],
  '/asset/pmv/requests/:id': ['pmv.request.view', 'pmv.view'],
  '/asset/pmv/allocations/:id': ['pmv.view', 'pmv.allocate'],
  '/asset/pmv/allocations/:id/receive': ['pmv.view', 'pmv.allocate'],
  '/asset/pmv/daily-logs': ['pmv.daily_log.view', 'pmv.view'],
  '/asset/pmv/daily-logs/:id': ['pmv.daily_log.view', 'pmv.view'],
  '/asset/pmv/daily-log-sheets': [
    'pmv.daily_log_sheet.view',
    'pmv.daily_log.view',
    'pmv.view',
  ],
  '/asset/pmv/daily-log-sheets/bulk': [
    'pmv.daily_log_sheet.view',
    'pmv.daily_log.view',
    'pmv.view',
  ],
  '/asset/pmv/daily-log-sheets/exceptions': [
    'pmv.daily_log_sheet.view',
    'pmv.daily_log.view',
    'pmv.view',
  ],
  '/asset/pmv/daily-log-sheets/:id': [
    'pmv.daily_log_sheet.view',
    'pmv.daily_log.view',
    'pmv.view',
  ],
  '/asset/pmv/hire': ['pmv.hire.view', 'pmv.hire.commercial_view', 'pmv.view'],
  '/asset/pmv/hire/control': ['pmv.hire.view', 'pmv.hire.commercial_view', 'pmv.view'],
  '/asset/pmv/hire/agreements': ['pmv.hire.view', 'pmv.hire.commercial_view', 'pmv.view'],
  '/asset/pmv/hire/agreements/:id': ['pmv.hire.view', 'pmv.hire.manage', 'pmv.view'],
  '/asset/pmv/hire/on-off': ['pmv.hire.view', 'pmv.hire.off_hire', 'pmv.hire.manage', 'pmv.view'],
  '/asset/pmv/hire/timesheets': ['pmv.hire.view', 'pmv.hire.timesheet.review', 'pmv.view'],
  '/asset/pmv/hire/timesheets/:id': ['pmv.hire.view', 'pmv.hire.timesheet.review', 'pmv.view'],
  '/asset/pmv/hire/statements': ['pmv.hire.view', 'pmv.hire.commercial_view', 'pmv.view'],
  '/asset/pmv/hire/statements/:id': ['pmv.hire.view', 'pmv.hire.commercial_view', 'pmv.view'],
  '/asset/pmv/hire/reconciliations': ['pmv.hire.view', 'pmv.hire.reconcile', 'pmv.view'],
  '/asset/pmv/hire/reconciliations/:id': ['pmv.hire.view', 'pmv.hire.reconcile', 'pmv.view'],
  '/asset/pmv/hire/attention': ['pmv.hire.view', 'pmv.hire.commercial_view', 'pmv.view'],
  '/prj/projects/:id/pmv': ['pmv.view', 'project.view'],
  '/mfg/control-tower': [
    'manufacturing.control_tower.view',
    'manufacturing.view',
    'manufacturing.reports.view',
  ],
  '/mfg/workstation-control-center': [
    'manufacturing.workstation.view',
    'manufacturing.view',
    'manufacturing.reports.view',
  ],
  '/mfg/dashboard': ['manufacturing.control_tower.manage', 'company.view'],
  '/mfg/reports': ['manufacturing.reports.view', 'manufacturing.view'],
  '/mfg/reports/work-order-register': ['manufacturing.reports.view', 'manufacturing.view'],
  '/mfg/reports/operations-status': ['manufacturing.reports.view', 'manufacturing.view'],
  '/mfg/work-orders': ['manufacturing.view'],
  '/mfg/route-sheet-register': ['manufacturing.route_sheet.view', 'manufacturing.view'],
  '/mfg/production-requests': ['manufacturing.view'],
  '/mfg/production-requests/new': ['manufacturing.view'],
  '/mfg/work-order-operations': ['manufacturing.view', 'manufacturing.wo.operate'],
  '/mfg/work-order-operations/eod-close': ['manufacturing.wo.eod_close', 'manufacturing.wo.complete'],
  '/mfg/factory-floor': ['manufacturing.view'],
  '/mfg/traceability-matrix': ['manufacturing.view', 'inventory.view'],
  '/mfg/boms': ['manufacturing.bom.manage', 'manufacturing.view'],
  '/mfg/boms/new': ['manufacturing.bom.manage', 'manufacturing.view'],
  '/mfg/workstations': ['manufacturing.view'],
  '/mfg/stage-master': ['manufacturing.view'],
  '/mfg/capacity-planning': ['manufacturing.view'],
  '/mfg/material-audit': ['manufacturing.view'],
  '/mfg/master-schedule': ['manufacturing.view'],

  // Finance
  '/fin/dashboard': ['finance.view'],
  '/fin/gl/chart-of-accounts': ['finance.coa.manage'],
  '/fin/gl/journals': ['finance.voucher.create', 'finance.view'],
  '/fin/proforma-invoices': ['growth.invoice.view', 'growth.view'],
  '/fin/receipt-vouchers': ['finance.view', 'finance.voucher.create'],
  '/fin/payment-vouchers': ['finance.view', 'finance.voucher.create'],
  '/fin/payment-vouchers/new': ['finance.voucher.create', 'finance.view'],
  '/finance/ap/supplier-advance-refunds': ['finance.view', 'finance.voucher.create'],
  '/fin/direct-purchase-invoices': ['direct_purchase_invoice.view', 'finance.view'],
  '/fin/reports/direct-purchase-invoice-register': ['direct_purchase_invoice.view', 'finance.reports.view'],
  '/fin/prepaid': ['finance.recognition.view'],
  '/fin/prepaid/new': ['finance.recognition.create'],
  '/fin/prepaid/contracts': ['finance.recognition.view'],
  '/fin/prepaid/runs': ['finance.recognition.view', 'finance.recognition.run', 'finance.recognition.post'],
  '/fin/prepaid/runs/new': ['finance.recognition.run'],
  '/fin/prepaid/reconciliation': ['finance.recognition.reconcile'],
  '/fin/prepaid/settings': ['finance.recognition.admin'],
  '/fin/prepaid/reports': ['finance.recognition.view'],
  '/fin/prepaid/reports/register': ['finance.recognition.view'],
  '/fin/prepaid/reports/schedule': ['finance.recognition.view'],
  '/fin/prepaid/reports/movement': ['finance.recognition.view'],
  '/fin/prepaid/reports/forecast': ['finance.recognition.view'],
  '/fin/debit-notes': ['finance.view'],
  '/fin/credit-notes': ['finance.view'],
  '/fin/currencies': ['finance.view'],
  '/fin/departments': ['finance.view'],
  '/fin/payment-terms': ['finance.view'],
  '/fin/banking/accounts': ['finance.view'],
  '/fin/reports': ['finance.reports.view', 'finance.view'],
  '/fin/reports/ledger': ['finance.reports.view', 'finance.view'],
  '/fin/reports/project-ledger': ['finance.reports.view', 'finance.view'],
  '/fin/reports/project-profit-loss': ['finance.reports.view', 'finance.view'],
  '/fin/reports/monthly-profit-loss': ['finance.reports.view', 'finance.view'],
  '/fin/reports/operational-profit': ['finance.reports.view', 'finance.view'],
  '/fin/reports/customer-outstanding': ['finance.reports.view'],
  '/fin/reports/supplier-outstanding': ['finance.reports.view'],
  '/fin/reports/customer-statement': ['finance.reports.view'],
  '/fin/reports/supplier-statement': ['finance.reports.view'],
  '/fin/reports/settlement-register': ['finance.reports.view', 'finance.view'],
  '/fin/reports/payment-register': ['finance.reports.view', 'finance.view'],
  '/fin/reports/receipt-register': ['finance.reports.view', 'finance.view'],
  '/fin/reports/supplier-advance-register': ['finance.reports.view', 'finance.view'],
  '/fin/tax/vat-return': ['finance.vatreturn.view', 'finance.view'],

  // QMS / GRC — Quality role uses quality.* (and moc.*); manufacturing.view kept for legacy MFG roles
  '/qms/dashboard': [
    'manufacturing.view',
    'quality.plan.view',
    'quality.inspection.view',
    'quality.ncr.view',
    'quality.capa.view',
    'quality.kpi.view',
  ],
  '/qms/reports': ['quality.inspection.view', 'manufacturing.view'],
  '/qms/quality-plans': ['manufacturing.view', 'quality.plan.view'],
  '/qms/inspections': [
    'manufacturing.view',
    'quality.inspection.view',
    'quality.stage_inspection.view',
    'quality.stage_inspection.complete',
  ],
  // Legacy QMS paths — kept so redirects still authorize before Navigate.
  '/qms/receiving-inspections': [
    'quality.receiving_inspection.view',
    'quality.inspection.view',
    'manufacturing.view',
  ],
  '/qms/receiving-inspections/:id': [
    'quality.receiving_inspection.view',
    'quality.inspection.view',
    'manufacturing.view',
  ],
  '/qms/receiving-dimensional-inspections': [
    'quality.receiving_dimensional_inspection.view',
    'quality.receiving_inspection.view',
    'quality.inspection.view',
    'manufacturing.view',
  ],
  '/qms/receiving-dimensional-inspections/:id': [
    'quality.receiving_dimensional_inspection.view',
    'quality.receiving_inspection.view',
    'quality.inspection.view',
    'manufacturing.view',
  ],
  '/qms/initial-inspection-requests': [
    'manufacturing.view',
    'quality.inspection.view',
    'quality.initial_inspection.view',
  ],
  '/qms/final-inspection-reports': [
    'manufacturing.view',
    'quality.inspection.view',
    'quality.final_inspection.view',
  ],
  '/qms/inspection-release-notes': [
    'manufacturing.view',
    'quality.inspection.view',
    'quality.inspection_release_note.view',
  ],
  '/qms/certificates-of-conformity': [
    'manufacturing.view',
    'quality.inspection.view',
    'quality.certificate_of_conformity.view',
  ],
  '/qms/equipments': ['calibration.equipment.view', 'calibration.schedule.view'],
  '/qms/tmmde': ['calibration.equipment.view', 'calibration.schedule.view'],
  '/qms/tmmde/impact': ['calibration.impact.view', 'calibration.impact.manage'],
  '/qms/tmmde/impact/:id': ['calibration.impact.view', 'calibration.impact.manage'],
  '/qms/dimensional-inspection': [
    'manufacturing.view',
    'quality.inspection.view',
    'quality.dimensional_inspection.view',
  ],
  '/qms/dimensional-inspection/new': [
    'manufacturing.view',
    'quality.inspection.view',
    'quality.dimensional_inspection.view',
  ],
  '/qms/ncrs': ['manufacturing.view', 'quality.ncr.view'],
  '/qms/capas': ['manufacturing.view', 'quality.capa.view'],
  '/qms/management-reviews': ['manufacturing.view', 'quality.management_review.view'],
  '/qms/management-reviews/:id': ['manufacturing.view', 'quality.management_review.view'],
  '/qms/document-management': ['manufacturing.view', 'quality.document_control.view'],
  '/qms/document-management/:id': ['manufacturing.view', 'quality.document_control.view'],
  '/qms/audits': [
    'manufacturing.view',
    'quality.plan.view',
    'quality.inspection.view',
    'quality.ncr.view',
    'quality.capa.view',
  ],
  '/grc/risk-assessments': ['risk.assessment.view', 'company.view', 'user.view'],
  '/grc/risk-assessments/:id': ['risk.assessment.view', 'company.view', 'user.view'],
  '/grc/moc': ['moc.view', 'company.view'],
  '/grc/moc/reports': ['moc.view', 'company.view'],
  '/grc/moc/new': ['moc.create', 'company.view'],
  '/grc/competency': COMPETENCY_VIEW_ANY,
  '/grc/competency/job-profiles': COMPETENCY_VIEW_ANY,
  '/grc/competency/matrix': COMPETENCY_VIEW_ANY,
  '/grc/competency/courses': [...COMPETENCY_TRAINING_VIEW, 'competency.effectiveness.evaluate'],
  '/grc/competency/schedule': COMPETENCY_TRAINING_VIEW,
  '/grc/competency/sessions': COMPETENCY_TRAINING_VIEW,
  '/grc/competency/cycles': ['competency.assessment.view', 'competency.assessment.close'],
  '/grc/competency/import': ['competency.import'],
  '/grc/calibration': [
    'manufacturing.view',
    'quality.inspection.view',
    'calibration.schedule.view',
    'calibration.record.view',
  ],
  '/grc/calibration/schedules/new': ['calibration.schedule.create', 'manufacturing.view', 'asset.create'],
  '/grc/calibration/schedules/:id/edit': ['calibration.schedule.edit'],
  '/grc/calibration/records/new': ['calibration.record.create', 'manufacturing.view', 'asset.create'],
  '/grc/calibration/records/:id/edit': ['calibration.record.create'],
  '/grc/calibration/records/:id': ['calibration.record.view', 'manufacturing.view', 'asset.view'],
  '/compliance/uae-einvoicing': ['compliance.uae.view', 'finance.view', 'growth.invoice.view'],
  '/compliance/inbound-einvoices': [
    'compliance.asp.inbound.view',
    'compliance.uae.view',
    'procurement.invoice.view',
  ],
  '/compliance/ksa-zatca': ['finance.view', 'growth.invoice.view'],

  // Projects / HR
  '/prj/projects': ['project.view'],
  '/prj/projects/new': ['project.create'],
  '/prj/budgets': ['project.view', 'project.budget.view', 'project.budget.edit', 'project.cost_control.view'],
  '/prj/projects/:id/budgets': ['project.view', 'project.budget.view', 'project.budget.edit', 'project.cost_control.view'],
  '/prj/projects/:id/budgets/variance': [
    'project.view',
    'project.budget.view',
    'project.cost_control.view',
  ],
  '/prj/budget-revisions/:revisionId': ['project.view', 'project.budget.view', 'project.budget.edit'],
  '/prj/budget-exceedances/:exceedanceId': [
    'project.view',
    'project.budget.view',
    'project.cost_control.view',
    'project.budget_exceedance.submit',
  ],
  '/epc': [
    'epc.view',
    'epc.admin',
    'epc.engineering',
    'epc.wbs',
    'epc.execution',
    'epc.execution.manage',
    'project.view',
  ],
  '/epc/execution': [
    'epc.execution',
    'epc.execution.manage',
    'epc.view',
    'epc.admin',
    'project.view',
  ],
  '/epc/commercial': [
    'epc.view',
    'epc.admin',
    'epc.tender.view',
    'epc.tender.manage',
    'epc.commercial.dashboard',
    'epc.prequalification.view',
    'epc.contract.view',
    'project.view',
  ],
  '/epc/commercial/prequalifications': [
    'epc.prequalification.view',
    'epc.prequalification.manage',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/invitations/new': [
    'epc.prequalification.view',
    'epc.prequalification.manage',
    'epc.tender.view',
    'epc.tender.manage',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/tenders': [
    'epc.tender.view',
    'epc.tender.manage',
    'epc.view',
    'epc.admin',
    'project.view',
  ],
  '/epc/libraries/wbs': [
    'epc.library.view',
    'epc.library.manage',
    'epc.estimate.view',
    'epc.admin',
    'epc.tender.manage',
    'epc.view',
  ],
  '/epc/libraries': [
    'epc.library.view',
    'epc.library.manage',
    'epc.estimate.view',
    'epc.admin',
    'epc.tender.manage',
    'epc.view',
  ],
  '/epc/libraries/labour': [
    'epc.library.view',
    'epc.library.manage',
    'epc.estimate.view',
    'epc.admin',
    'epc.tender.manage',
    'epc.view',
  ],
  '/epc/libraries/equipment': [
    'epc.library.view',
    'epc.library.manage',
    'epc.estimate.view',
    'epc.admin',
    'epc.tender.manage',
    'epc.view',
  ],
  '/epc/libraries/crews': [
    'epc.library.view',
    'epc.library.manage',
    'epc.estimate.view',
    'epc.admin',
    'epc.tender.manage',
    'epc.view',
  ],
  '/epc/libraries/norms': [
    'epc.library.view',
    'epc.library.manage',
    'epc.estimate.view',
    'epc.admin',
    'epc.tender.manage',
    'epc.view',
  ],
  '/epc/libraries/assemblies': [
    'epc.library.view',
    'epc.library.manage',
    'epc.estimate.view',
    'epc.admin',
    'epc.tender.manage',
    'epc.view',
  ],
  '/epc/libraries/rate-resolver': [
    'epc.library.view',
    'epc.library.manage',
    'epc.estimate.view',
    'epc.admin',
    'epc.tender.manage',
    'epc.view',
  ],
  '/epc/commercial/bid-decisions': [
    'epc.tender.view',
    'epc.tender.manage',
    'epc.bid_decision.submit',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/awards': [
    'epc.tender.view',
    'epc.award.manage',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/contracts': [
    'epc.contract.view',
    'epc.contract.manage',
    'epc.contract_admin.view',
    'epc.contract_admin.manage',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/contracts/new': ['epc.contract.manage', 'epc.admin'],
  '/epc/ipc': [
    'epc.payment_application.view',
    'epc.payment_application.manage',
    'epc.view',
    'epc.admin',
  ],
  '/epc/ipc/certifications': [
    'epc.certification.view',
    'epc.certification.manage',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/contracts/:id': [
    'epc.contract.view',
    'epc.contract.manage',
    'epc.contract_admin.view',
    'epc.contract_admin.manage',
    'epc.view',
    'epc.admin',
    'project.view',
  ],
  '/epc/commercial/contracts/:id/payment-applications/new': [
    'epc.payment_application.manage',
    'epc.contract_admin.manage',
    'epc.contract.manage',
    'epc.admin',
  ],
  '/epc/commercial/contracts/:id/payment-applications/:paId': [
    'epc.payment_application.view',
    'epc.payment_application.manage',
    'epc.contract_admin.view',
    'epc.contract_admin.manage',
    'epc.contract.view',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/contracts/:id/control': [
    'epc.commercial_control.view',
    'epc.contract_admin.view',
    'epc.contract.view',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/subcontracts': [
    'epc.subcontract_admin.view',
    'epc.subcontract_admin.manage',
    'epc.subcontract_agreement.view',
    'epc.subcontract_agreement.manage',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/subcontracts/new': [
    'epc.subcontract_admin.manage',
    'epc.subcontract_agreement.manage',
    'epc.admin',
  ],
  '/epc/commercial/subcontracts/:id/edit': [
    'epc.subcontract_admin.manage',
    'epc.subcontract_agreement.manage',
    'epc.admin',
  ],
  '/epc/commercial/subcontracts/:id': [
    'epc.subcontract_admin.view',
    'epc.subcontract_admin.manage',
    'epc.subcontract_agreement.view',
    'epc.subcontract_agreement.manage',
    'epc.view',
    'epc.admin',
    'project.view',
  ],
  '/epc/commercial/subcontracts/:id/control': [
    'epc.subcontract_admin.view',
    'epc.subcontract_agreement.view',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/subcontracts/:id/certificates/new': [
    'epc.subcontract_certificate.manage',
    'epc.subcontract_admin.manage',
    'epc.admin',
  ],
  '/epc/commercial/subcontracts/:id/certificates/:spcId': [
    'epc.subcontract_certificate.view',
    'epc.subcontract_certificate.manage',
    'epc.subcontract_admin.view',
    'epc.subcontract_admin.manage',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/mobilizations': [
    'epc.contract.view',
    'epc.mobilization.manage',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/control-tower': [
    'epc.commercial.dashboard',
    'epc.view',
    'epc.admin',
  ],
  '/epc/commercial/pursuits/:opportunityId': [
    'epc.view',
    'epc.admin',
    'epc.tender.view',
    'epc.tender.manage',
    'epc.commercial.dashboard',
    'epc.prequalification.view',
    'epc.contract.view',
    'project.view',
  ],
  '/epc/commercial/pursuits/resolve/:entityType/:entityId': [
    'epc.view',
    'epc.admin',
    'epc.tender.view',
    'epc.tender.manage',
    'epc.commercial.dashboard',
    'epc.prequalification.view',
    'epc.contract.view',
    'project.view',
  ],
  '/prj/project-health': ['project.view'],
  '/prj/timesheets': ['project.view'],
  '/prj/costs': ['project.view', 'project.costing.view'],
  '/prj/dashboard': ['project.view', 'project.reports.view'],
  '/prj/reports': ['project.reports.view', 'project.view'],
  '/prj/reports/financial-summary': ['project.reports.view', 'project.view'],
  '/prj/reports/trial-balance': ['project.reports.view', 'project.view', 'project.cost_control.view'],
  '/prj/reports/ledger': ['project.reports.view', 'project.view', 'project.cost_control.view'],
  '/prj/reports/budget-vs-commitment': ['project.reports.view', 'project.view', 'project.cost_control.view'],
  '/prj/reports/budget-vs-actual': ['project.reports.view', 'project.view', 'project.cost_control.view'],
  '/prj/reports/budget-vs-commitment-vs-actual': [
    'project.reports.view',
    'project.view',
    'project.cost_control.view',
  ],
  '/prj/reports/cash-flow': ['project.reports.view', 'project.view', 'project.cost_control.view'],
  '/prj/reports/cost-analysis': ['project.reports.view', 'project.view', 'project.cost_control.view'],
  '/prj/reports/profitability': ['project.reports.view', 'project.view', 'project.cost_control.view'],
  '/prj/reports/billing-vs-cost': ['project.reports.view', 'project.view', 'project.cost_control.view'],
  '/prj/reports/original-vs-current-vs-actual': [
    'project.reports.view',
    'project.view',
    'project.cost_control.view',
  ],
  '/prj/reports/at-risk': ['project.reports.view', 'project.view', 'project.cost_control.view'],
  '/prj/reports/open-commitments': ['project.reports.view', 'project.view', 'project.cost_control.view'],
  '/prj/reports/retention-vo-claim-aging': ['project.reports.view', 'project.view'],
  '/epc/reports': ['project.reports.view', 'project.view'],
  '/epc/reports/ipc-register': ['project.reports.view', 'project.view'],
  '/epc/reports/sca-register': ['project.reports.view', 'project.view'],
  '/hr/dashboard': ['hr.view', 'hr.employee.create', 'hr.employee.edit', 'company.view', 'user.view'],
  '/hr/reports': ['hr.reports.view', 'hr.view'],
  '/hr/reports/headcount': ['hr.reports.view', 'hr.view'],
  '/hr/reports/attendance': ['hr.reports.view', 'hr.view', 'hr.attendance.view'],
  '/hr/reports/leave-liability': ['hr.reports.view', 'hr.view', 'hr.leave.view'],
  '/hr/reports/ot': ['hr.reports.view', 'hr.view', 'hr.ot.manage'],
  '/hr/reports/payroll-register': ['hr.reports.view', 'hr.view', 'hr.payroll.view'],
  '/hr/reports/loans': ['hr.reports.view', 'hr.view', 'hr.loan.manage'],
  '/hr/reports/document-expiry': ['hr.reports.view', 'hr.view'],
  '/hr/reports/eosb': ['hr.reports.view', 'hr.view', 'hr.eosb.manage'],
  '/hr/reports/allocations': ['hr.reports.view', 'hr.view', 'hr.allocation.manage'],
  '/hr/reports/project-manpower': ['hr.reports.view', 'hr.view', 'hr.allocation.manage'],
  '/hr/reports/allocation-exceptions': ['hr.reports.view', 'hr.view', 'hr.allocation.manage'],
  '/hr/reports/shift-roster': ['hr.reports.view', 'hr.view', 'hr.shift.manage'],
  '/hr/reports/timesheet-vs-allocation': ['hr.reports.view', 'hr.view', 'hr.allocation.manage'],
  '/hr/reports/asset-custody': ['hr.reports.view', 'hr.view', 'hr.asset_custody.manage'],
  '/hr/setup': ['hr.setup.view', 'hr.setup.manage', 'hr.view'],
  '/hr/accruals': [
    'hr.accrual.view',
    'hr.accrual.manage',
    'hr.accrual.accounting.manage',
    'hr.accrual.accounting.approve',
    'hr.accrual.reconcile',
    'hr.setup.view',
    'hr.setup.manage',
    'hr.leave.view',
    'hr.leave.manage',
    'hr.eosb.manage',
    'hr.view',
  ],
  '/hr/holidays': ['hr.holiday.manage', 'hr.setup.view', 'hr.view'],
  '/hr/shifts': ['hr.shift.manage', 'hr.view'],
  '/hr/locations': ['hr.setup.view', 'hr.setup.manage', 'hr.view'],
  '/hr/positions': ['hr.setup.view', 'hr.setup.manage', 'hr.view'],
  '/hr/leave-types': ['hr.setup.view', 'hr.setup.manage', 'hr.leave.view', 'hr.view'],
  '/hr/leave-opening-balances': ['hr.leave.manage', 'hr.employee.edit', 'hr.view'],
  '/hr/grades': ['hr.setup.view', 'hr.setup.manage', 'hr.view'],
  '/hr/reports/late-register': ['hr.reports.view', 'hr.view', 'hr.attendance.view'],
  '/hr/reports/project-labour': ['hr.reports.view', 'hr.view'],
  '/hr/reports/eosb-settlement': ['hr.reports.view', 'hr.view', 'hr.eosb.manage'],
  '/hr/reports/benefit-valuation-register': [
    'hr.reports.view',
    'hr.view',
    'hr.accrual.view',
    'hr.accrual.reconcile',
  ],
  '/hr/reports/leave-liability-movement': ['hr.reports.view', 'hr.view', 'hr.accrual.view'],
  '/hr/reports/eosb-liability-movement': [
    'hr.reports.view',
    'hr.view',
    'hr.eosb.manage',
    'hr.accrual.view',
  ],
  '/hr/reports/accrual-to-jv-register': [
    'hr.reports.view',
    'hr.view',
    'hr.accrual.accounting.manage',
  ],
  '/hr/reports/hrms-gl-reconciliation': ['hr.reports.view', 'hr.view', 'hr.accrual.reconcile'],
  '/hr/reports/accrual-exceptions-audit': ['hr.reports.view', 'hr.view', 'hr.accrual.reconcile'],
  '/hr/salary-heads': ['hr.salary_head.manage', 'hr.setup.view', 'hr.view'],
  '/hr/me': ['hr.ess.view', 'hr.ess.request', 'hr.view'],
  '/hr/me/leave': ['hr.ess.view', 'hr.ess.request', 'hr.view'],
  '/hr/me/ot': ['hr.ess.view', 'hr.ess.request', 'hr.view'],
  '/hr/me/payslips': ['hr.ess.view', 'hr.ess.request', 'hr.view'],
  '/hr/me/profile': ['hr.ess.view', 'hr.ess.request', 'hr.view'],
  '/hr/me/letters': ['hr.ess.view', 'hr.ess.request', 'hr.view'],
  '/hr/me/timesheets': ['hr.ess.view', 'hr.ess.request', 'hr.view'],
  '/hr/letter-requests': ['hr.view', 'hr.employee.edit', 'hr.ess.view'],
  '/hr/employees': ['hr.view', 'company.view', 'user.view'],
  '/hr/employees/new': ['hr.employee.create'],
  '/hr/employees/location-assignment': ['hr.employee.edit', 'hr.view'],
  '/hr/employees/shift-assignment': ['hr.shift.manage', 'hr.employee.edit', 'hr.view'],
  '/hr/contracts': ['hr.contract.manage', 'hr.view'],
  '/hr/contracts/new': ['hr.contract.manage', 'hr.view'],
  '/hr/salary-revisions': ['hr.salary.revise', 'hr.view'],
  '/hr/salary-revisions/new': ['hr.salary.revise', 'hr.employee.edit', 'hr.view'],
  '/hr/salary-revisions/:id/edit': ['hr.salary.revise', 'hr.employee.edit', 'hr.view'],
  '/hr/profile-change-requests': ['hr.view', 'company.view', 'user.view'],
  '/hr/allocations': ['hr.allocation.manage', 'hr.view'],
  '/hr/allocations/bulk': ['hr.allocation.manage', 'hr.view'],
  '/hr/asset-custody': ['hr.asset_custody.manage', 'hr.view'],
  '/hr/asset-custody/new': ['hr.asset_custody.manage', 'hr.view'],
  '/hr/attendance': ['hr.attendance.view', 'hr.attendance.manage', 'hr.view'],
  '/hr/attendance/months': ['hr.attendance.view', 'hr.attendance.manage', 'hr.view'],
  '/hr/attendance/generate': ['hr.attendance.manage'],
  '/hr/overtime': ['hr.ot.manage', 'hr.view'],
  '/hr/overtime/new': ['hr.ot.manage', 'hr.view'],
  '/hr/leaves': ['hr.leave.view', 'hr.leave.manage', 'hr.view'],
  '/hr/leaves/new': ['hr.leave.view', 'hr.leave.manage', 'hr.view'],
  '/hr/leaves/bulk': ['hr.leave.manage', 'hr.view'],
  '/hr/leave-settlements': ['hr.leave.manage', 'hr.leave.view', 'hr.view'],
  '/hr/adjustments': ['hr.adjustment.manage', 'hr.view'],
  '/hr/adjustments/new': ['hr.adjustment.manage', 'hr.view'],
  '/hr/loans': ['hr.loan.manage', 'hr.view'],
  '/hr/loans/new': ['hr.loan.manage', 'hr.view'],
  '/hr/payroll': ['hr.payroll.view', 'hr.payroll.pay', 'hr.view'],
  '/hr/payroll/periods/new': ['hr.payroll.pay', 'hr.payroll.view', 'hr.view'],
  '/hr/eosb': ['hr.eosb.manage', 'hr.view'],
  '/hr/eosb/new': ['hr.eosb.manage', 'hr.view'],
  '/hr/eosb/profile': ['hr.eosb.manage', 'hr.view'],
  '/hr/reports/eosb-entitlement-statement': ['hr.reports.view', 'hr.view', 'hr.eosb.manage'],
  '/hr/document-expiry': ['hr.view'],
  '/hr/audit-log': ['hr.setup.view', 'hr.view'],
  '/hr/documents': ['hr.view', 'document.view'],
  '/hr/recruitment': ['hr.recruit.view', 'hr.recruit.manage', 'hr.onboarding.manage', 'hr.view'],
  '/hr/requisitions': ['hr.recruit.view', 'hr.recruit.manage', 'hr.view'],
  '/hr/requisitions/new': ['hr.recruit.manage'],
  '/hr/jobs': ['hr.recruit.view', 'hr.recruit.manage', 'hr.view'],
  '/hr/jobs/new': ['hr.recruit.manage'],
  '/hr/candidates': ['hr.recruit.view', 'hr.recruit.manage', 'hr.view'],
  '/hr/candidates/new': ['hr.recruit.manage'],
  '/hr/candidates/import': ['hr.recruit.manage'],
  '/hr/applications': ['hr.recruit.view', 'hr.recruit.manage', 'hr.view'],
  '/hr/applications/new': ['hr.recruit.manage'],
  '/hr/interviews': ['hr.recruit.view', 'hr.recruit.manage', 'hr.view'],
  '/hr/interviews/new': ['hr.recruit.manage'],
  '/hr/interviews/mine': ['hr.recruit.interview.feedback', 'hr.recruit.manage'],
  '/hr/assessments': ['hr.recruit.view', 'hr.recruit.manage', 'hr.view'],
  '/hr/assessments/new': ['hr.recruit.manage'],
  '/hr/offers': ['hr.recruit.view', 'hr.recruit.manage', 'hr.recruit.convert', 'hr.view'],
  '/hr/offers/new': ['hr.recruit.manage'],
  '/hr/onboarding': ['hr.recruit.view', 'hr.recruit.manage', 'hr.onboarding.manage', 'hr.view'],
  '/hr/onboarding/new': ['hr.onboarding.manage', 'hr.recruit.manage'],
  '/hr/onboarding/my-tasks': ['hr.onboarding.task.complete', 'hr.onboarding.manage', 'hr.recruit.manage'],
  '/hr/careers-settings': ['hr.recruit.manage', 'hr.recruit.view'],
  '/hr/recruit-reports': ['hr.recruit.view', 'hr.recruit.manage', 'hr.reports.view'],
  '/hr/reports/recruit-funnel': ['hr.reports.view', 'hr.recruit.view', 'hr.recruit.manage'],
  '/hr/reports/recruit-time-to-hire': ['hr.reports.view', 'hr.recruit.view', 'hr.recruit.manage'],
  '/hr/reports/recruit-sources': ['hr.reports.view', 'hr.recruit.view', 'hr.recruit.manage'],
  '/hr/reports/recruit-offer-acceptance': ['hr.reports.view', 'hr.recruit.view', 'hr.recruit.manage'],
  '/hr/reports/recruit-workload': ['hr.reports.view', 'hr.recruit.view', 'hr.recruit.manage'],
  '/hr/reports/recruit-requisition-ageing': ['hr.reports.view', 'hr.recruit.view', 'hr.recruit.manage'],
  '/hr/reports/recruit-onboarding': ['hr.reports.view', 'hr.recruit.view', 'hr.recruit.manage', 'hr.onboarding.manage'],
  '/platform/documents': ['document.view'],
  '/documents': ['document.view'],

  // Analytics — executive dashboard only; domain analytics use report-level permissions.
  '/analytics/executive': [
    'growth.reports.view',
    'growth.dashboard.view',
    'manufacturing.reports.view',
    'finance.reports.view',
  ],
  '/analytics/sales': ['growth.reports.view', 'growth.dashboard.view'],
  '/analytics/scm': ['procurement.reports.view'],
  '/analytics/mfg': ['manufacturing.reports.view'],
  '/analytics/quality': ['manufacturing.reports.view'],

  // Enterprise Memory (Phase 5.1)
  '/ai/workspace': ['user.view', 'company.view'],
  '/ai/udq': ['user.view', 'company.view'],
  '/ai/udq/:id': ['user.view', 'company.view'],
  '/ai/dashboard': ['user.view', 'company.view'],
  // Help Center — same capability as the ERP Assistant how-to answers (GET /ai-agent/help/guides).
  '/help': ['user.view', 'company.view'],
  '/help/:guideId': ['user.view', 'company.view'],
  '/ai/enterprise-memory': ['ai.memory.view', 'ai.memory.search', 'company.view', 'user.view'],
  '/ai/enterprise-memory/:id': ['ai.memory.view', 'ai.memory.search', 'company.view', 'user.view'],

  // Owner Intelligence — module permission required (company.view is not sufficient).
  '/owner-intelligence': ['owner_intelligence.view'],
  '/owner-intelligence/group': ['owner_intelligence.view'],
  '/owner-intelligence/business-health': ['owner_intelligence.health.view', 'owner_intelligence.view'],
  '/owner-intelligence/financial-health': ['owner_intelligence.health.view', 'owner_intelligence.view'],
  '/owner-intelligence/profit': ['owner_intelligence.view'],
  '/owner-intelligence/cash': ['owner_intelligence.view'],
  '/owner-intelligence/customers': ['owner_intelligence.view'],
  '/owner-intelligence/sales-pipeline': ['owner_intelligence.view', 'growth.view'],
  '/owner-intelligence/operations': ['owner_intelligence.view'],
  '/owner-intelligence/supply-chain': ['owner_intelligence.view'],
  '/owner-intelligence/kpis': ['owner_intelligence.kpi.view', 'owner_intelligence.view'],
  '/owner-intelligence/strategy': ['owner_intelligence.kpi.view', 'owner_intelligence.view'],
  '/owner-intelligence/enterprise-strategy': [
    'strategy.view',
    'strategy.manage',
    'strategy.board',
    'strategy.executive',
    'owner_intelligence.view',
  ],
  '/owner-intelligence/enterprise-readiness': [
    'readiness.view',
    'readiness.admin',
    'readiness.perf',
    'owner_intelligence.view',
  ],
  '/owner-intelligence/actions': ['owner_intelligence.actions.manage', 'owner_intelligence.view'],
  '/owner-intelligence/benchmarks': ['owner_intelligence.view'],
  '/owner-intelligence/root-cause': ['owner_intelligence.view'],
  '/owner-intelligence/risk': ['owner_intelligence.view', 'eoip.view'],
  '/owner-intelligence/operational-intelligence': [
    'eoip.view',
    'eoip.feed',
    'owner_intelligence.view',
  ],
  '/owner-intelligence/operational-actions': [
    'eoaf.view',
    'eoaf.manage',
    'eoip.view',
    'owner_intelligence.view',
  ],
  '/owner-intelligence/digital-twin': [
    'edt.view',
    'edt.simulate',
    'owner_intelligence.view',
    'eoip.view',
  ],
  '/owner-intelligence/module-intelligence': [
    'intelligence.view',
    'intelligence.sidebar',
    'owner_intelligence.view',
    'eoip.view',
  ],
  '/owner-intelligence/automation': [
    'automation.view',
    'automation.run',
    'eoaf.view',
    'owner_intelligence.view',
  ],
  '/owner-intelligence/automation/designer': [
    'automation.design',
    'automation.admin',
    'owner_intelligence.view',
  ],
  '/owner-intelligence/scenarios': ['owner_intelligence.view'],
  '/owner-intelligence/advisor': ['owner_intelligence.advisor.use', 'owner_intelligence.view'],
  '/owner-intelligence/ai-governance': ['owner_intelligence.view'],
  '/owner-intelligence/organizational-knowledge': [
    'ai.memory.health.view',
    'ai.memory.view',
    'owner_intelligence.view',
  ],
  '/owner-intelligence/memory': ['owner_intelligence.board.view', 'owner_intelligence.view'],
  '/owner-intelligence/meetings': ['owner_intelligence.board.view', 'owner_intelligence.view'],
  '/owner-intelligence/external-context': ['owner_intelligence.view'],
  '/owner-intelligence/board-packs': ['owner_intelligence.board.view', 'owner_intelligence.view'],

  // Platform
  '/platform/security/users': ['user.view', 'role.view'],
  '/platform/sales-teams': ['growth.team.manage'],
  '/platform/ai-agent/audit': ['user.view'],
  '/platform/ai-agent/config': ['user.view'],
  '/platform/recognition/lab': ['recognition.lab', 'recognition.view'],
  '/platform/recognition/analytics': ['recognition.analytics', 'recognition.view'],
  '/platform/workflows/approvals': [
    'settings.workflows.view',
    'settings.workflows.create',
    'settings.workflows.edit',
    'settings.workflows.delete',
  ],
  '/platform/workflows/approvals/new': ['settings.workflows.create', 'settings.workflows.edit'],
  '/platform/workflows/rejection-reasons': [
    'settings.workflows.view',
    'settings.workflows.edit',
  ],
  '/platform/rejection-register': ['settings.workflows.view'],
  '/platform/companies': ['platform.tenants.view'],
  '/platform/companies/new': ['platform.tenants.create', 'platform.tenants.view'],
  '/platform/setup/company': ['company.view'],
  '/platform/setup/configuration-studio': [
    'configuration.view',
    'configuration.admin',
    'company.view',
  ],
  '/platform/setup/system': ['user.view'],
  '/platform/setup/api-keys': ['company.api_key.view', 'company.api_key.manage'],
  '/settings/api-keys': ['company.api_key.view', 'company.api_key.manage'],
  '/platform/setup/status': ['user.view'],
  '/platform/setup/sequences': ['user.view'],
  '/platform/setup/responsibility-rules': [
    'platform.responsibility_rule.view',
    'platform.responsibility_rule.manage',
  ],
  '/platform/setup/email-templates': ['user.view'],
  '/platform/setup/task-sla': ['user.view'],
  '/platform/foundation/customers': ['growth.customer.view'],
  '/platform/foundation/suppliers': ['procurement.supplier.view'],
  '/platform/foundation/suppliers/:id': ['procurement.supplier.view'],
  '/platform/foundation/suppliers/:id/360': ['procurement.supplier.view'],
  '/platform/foundation/supplier-categories': ['procurement.supplier.view'],
  '/platform/foundation/warehouses': ['inventory.view'],
  '/platform/foundation/currencies': ['finance.view'],
  // Revenue service types: not listed here so any authenticated user sees the menu link (same as unlisted routes).
  // API remains protected by backend (platform.revenue_service_type.view / company.view / finance.view).

  // Create menu
  '/growth/orders/new': ['growth.order.create'],
  '/scm/procurement/purchase-orders/new': ['procurement.po.create'],
  '/scm/procurement/grn/new': ['procurement.grn.create', 'procurement.po.create'],
  '/scm/procurement/service-receipts/new': ['procurement.grn.create', 'procurement.po.create'],
  '/scm/procurement/purchase-returns/new': ['procurement.po.view'],
  '/scm/inventory/material-requests/new': ['inventory.material_request.create'],
  '/scm/inventory/material-requests/:id/edit': ['inventory.material_request.edit'],
  '/mfg/work-orders/new': ['manufacturing.wo.create'],
  '/fin/gl/journals/new': ['finance.voucher.create'],
  '/fin/debit-notes/new': ['finance.view'],
  '/qms/ncrs/new': ['manufacturing.view', 'quality.ncr.create', 'quality.ncr.view'],
  '/qms/ncrs/:id/edit': ['manufacturing.view', 'quality.ncr.create', 'quality.ncr.view'],
  '/qms/capas/new': ['manufacturing.view', 'quality.capa.create', 'quality.capa.view'],
  '/qms/capas/:id/edit': ['manufacturing.view', 'quality.capa.edit', 'quality.capa.create', 'quality.capa.view'],
  '/qms/management-reviews/new': [
    'manufacturing.view',
    'quality.management_review.create',
    'quality.management_review.view',
  ],
  '/qms/management-reviews/:id/edit': [
    'manufacturing.view',
    'quality.management_review.edit',
    'quality.management_review.create',
    'quality.management_review.view',
  ],
  '/qms/document-management/new': [
    'manufacturing.view',
    'quality.document_control.create',
    'quality.document_control.view',
  ],
  '/qms/document-management/:id/edit': [
    'manufacturing.view',
    'quality.document_control.edit',
    'quality.document_control.create',
    'quality.document_control.view',
  ],
  '/print/qms/capas/:id': ['manufacturing.view', 'quality.capa.view'],

  // Command palette (legacy/alternate paths)
  '/crm/leads': ['crm.lead.view'],
  '/crm/opportunities': ['crm.opportunity.view'],
  '/crm/estimations': ['crm.estimation.view'],
  '/estimation/boq': ['crm.estimation.view'],
  '/estimation/ai-predictor': ['crm.estimation.view'],
  '/estimation/version-comparison': ['crm.estimation.view'],
  '/estimation/margin-rules': ['crm.estimation.view'],
  '/estimation/historical-analysis': ['crm.estimation.view'],
  '/procurement/suppliers': ['procurement.supplier.view'],
  '/procurement/requisitions': ['procurement.pr.view'],
  '/procurement/rfqs': ['procurement.rfq.view'],
  '/procurement/orders': ['procurement.po.view'],
  '/procurement/grns': ['procurement.grn.view', 'procurement.po.view', 'procurement.view'],
  '/inventory/items': ['inventory.view'],
  '/inventory/warehouses': ['inventory.view'],
  '/inventory/stock-summary': ['inventory.view'],
  '/inventory/transfers': ['inventory.transfer.create', 'inventory.view'],
  '/inventory/adjustments': ['inventory.adjustment.create', 'inventory.view'],
  '/mfg/operations': ['manufacturing.view', 'growth.view'],
  '/mfg/operations/new': ['manufacturing.view', 'growth.view'],
  '/mfg/shifts': ['manufacturing.view'],
  '/mfg/calendar': ['manufacturing.view'],
  '/mfg/job-scheduling': ['manufacturing.view'],
  '/mfg/bom-routing': ['manufacturing.view', 'manufacturing.bom.manage'],
  '/mfg/operation-dependencies': ['manufacturing.view'],
  '/mfg/operation-tracking': ['manufacturing.view'],
  '/mfg/reports/outsourced-fabrication': [
    'manufacturing.outsourced_fabrication.reports',
    'manufacturing.reports.view',
    'manufacturing.view',
  ],
  '/fin/gl/journal-vouchers': ['finance.voucher.create', 'finance.view'],
  '/fin/banking/bank-accounts': ['finance.view'],
  '/fin/setup/currencies': ['finance.view'],
  '/qms/kpis': ['manufacturing.view', 'quality.kpi.view'],
  '/qms/kpi-measurements': ['manufacturing.view', 'quality.kpi.view', 'quality.kpi.measure'],
  '/risk/assessments': ['risk.assessment.view', 'company.view', 'user.view'],
  '/competency': COMPETENCY_VIEW_ANY,
  '/projects/list': ['project.view'],
  '/projects/timesheets': ['project.view'],
  '/projects/costs': ['project.costing.view', 'project.view'],
  '/projects/reports': ['project.reports.view'],
  '/settings/company': ['company.view'],
  '/settings/users': ['user.view'],
  '/settings/system': ['user.view', 'company.view'],
  '/education': ['education.access', 'education.admin'],
  '/education/settings': ['education.access', 'education.admin'],
  '/education/students': [
    'education.student.view',
    'education.student.view_all',
    'education.student.manage',
    'education.guardian.manage',
    'education.staff.manage',
  ],
  '/education/students/new': ['education.student.manage'],
  '/education/academic': ['education.academic.view', 'education.academic.manage', 'education.policy.publish'],
  '/education/academic/sessions/new': ['education.academic.manage'],
  '/education/policy': ['education.academic.view', 'education.academic.manage', 'education.policy.publish'],
  '/education/admissions': ['education.admission.view', 'education.admission.manage', 'education.admission.decide'],
  '/education/admissions/crm': ['education.admission.view', 'education.admission.manage', 'education.admission.decide'],
  '/education/enrollments': ['education.enrollment.view', 'education.enrollment.manage', 'education.enrollment.approve', 'education.section.allocate', 'education.withdrawal.manage'],
  '/education/enrollments/new': ['education.enrollment.manage'],
  '/education/progression': ['education.progression.manage', 'education.enrollment.view'],
  '/education/transfer-certificates': ['education.transfer_certificate.issue', 'education.enrollment.view'],
  '/education/attendance': ['education.attendance.view', 'education.attendance.mark', 'education.attendance.manage', 'education.attendance.correct', 'education.leave.manage'],
  '/education/attendance/settings': ['education.attendance.manage'],
  '/education/admissions/new': ['education.admission.manage'],
  '/education/admissions/enquiries': ['education.admission.view', 'education.admission.manage', 'education.admission.decide'],
  '/education/admissions/enquiries/new': ['education.admission.manage'],
  '/education/attendance/calendar': ['education.attendance.view', 'education.attendance.mark', 'education.attendance.manage', 'education.attendance.correct'],
  '/education/health': ['education.health.view', 'education.health.edit'],
  '/education/counseling': ['education.counseling.view', 'education.counseling.edit'],
  '/education/safeguarding': ['education.safeguarding.view', 'education.safeguarding.edit'],
  '/education/behaviour': ['education.behaviour.view', 'education.behaviour.manage', 'education.admin'],
  '/education/inclusion': ['education.inclusion.view', 'education.inclusion.manage', 'education.admin'],
  '/education/intervention': [
    'education.intervention.view',
    'education.intervention.manage',
    'education.admin',
  ],
  '/education/standards': ['education.standards.view', 'education.standards.manage', 'education.admin'],
  '/education/governance': [
    'education.governance.view',
    'education.governance.manage',
    'education.admin',
  ],
  '/education/app': ['education.app.view', 'education.app.manage', 'education.admin'],
  '/education/eqams': ['education.eqams.view', 'education.eqams.manage', 'education.admin'],
  '/education/portal/parent': ['education.portal.parent', 'education.admin'],
  '/education/portal/student': ['education.portal.student', 'education.admin'],
  '/education/portal/teacher': [
    'education.portal.teacher',
    'education.timetable.view',
    'education.attendance.view',
    'education.attendance.mark',
    'education.admin',
  ],
  '/education/udise': ['education.udise.view', 'education.udise.manage', 'education.admin'],
  '/education/ids': ['education.ids.view', 'education.ids.manage', 'education.admin'],
  '/education/comms': ['education.comms.view', 'education.comms.manage', 'education.admin'],
  '/education/print': [
    'education.transfer_certificate.issue',
    'education.enrollment.view',
    'education.fee.view',
    'education.fee.collect',
    'education.fee.manage',
    'education.student.view',
    'education.student.view_all',
    'education.attendance.view',
    'education.admin',
  ],
  '/education/pulse': ['education.pulse.view', 'education.admin'],
  '/education/quality-copilot': ['education.quality_copilot.view', 'education.admin'],
  '/education/process-insights': ['education.process_insights.view', 'education.admin'],
  '/education/reports': [
    'education.reports.view',
    'education.fee.view',
    'education.attendance.view',
    'education.academic.view',
    'education.admin',
  ],
  '/education/reports/strength': [
    'education.reports.view',
    'education.academic.view',
    'education.admin',
  ],
  '/education/reports/attendance': [
    'education.reports.view',
    'education.attendance.view',
    'education.attendance.mark',
    'education.admin',
  ],
  '/education/reports/fee-aging': [
    'education.reports.view',
    'education.fee.view',
    'education.fee.collect',
    'education.admin',
  ],
  '/education/fees': [
    'education.fee.view',
    'education.fee.manage',
    'education.fee.demand',
    'education.fee.collect',
    'education.fee.concession',
    'education.fee.promise',
    'education.fee.dispute',
    'education.admin',
  ],
  '/education/fees/heads': ['education.fee.view', 'education.fee.manage', 'education.admin'],
  '/education/fees/structures': ['education.fee.view', 'education.fee.manage', 'education.admin'],
  '/education/fees/demands': [
    'education.fee.view',
    'education.fee.demand',
    'education.fee.collect',
    'education.admin',
  ],
  '/education/fees/outstanding': [
    'education.fee.view',
    'education.fee.demand',
    'education.fee.collect',
    'education.admin',
  ],
  '/education/fees/collection': [
    'education.fee.view',
    'education.fee.demand',
    'education.fee.collect',
    'education.fee.promise',
    'education.admin',
  ],
  '/education/fees/family-statement': [
    'education.fee.view',
    'education.fee.collect',
    'education.fee.demand',
    'education.admin',
  ],
  '/education/fees/promises': [
    'education.fee.view',
    'education.fee.promise',
    'education.fee.collect',
    'education.admin',
  ],
  '/education/fees/disputes': [
    'education.fee.view',
    'education.fee.dispute',
    'education.fee.collect',
    'education.admin',
  ],
  '/education/fees/concessions': ['education.fee.view', 'education.fee.concession', 'education.admin'],
  '/education/transport': [
    'education.transport.view',
    'education.transport.manage',
    'education.admin',
  ],
  '/education/transport/routes': [
    'education.transport.view',
    'education.transport.manage',
    'education.admin',
  ],
  '/education/transport/enrollments': [
    'education.transport.view',
    'education.transport.manage',
    'education.admin',
  ],
  '/education/transport/enrollments/new': ['education.transport.manage', 'education.admin'],
  '/education/transport/enrollments/bulk': ['education.transport.manage', 'education.admin'],
  '/education/transport/boarding': [
    'education.transport.view',
    'education.transport.manage',
    'education.admin',
  ],
  '/education/library': [
    'education.library.view',
    'education.library.manage',
    'education.library.circulate',
    'education.admin',
  ],
  '/education/library/titles': [
    'education.library.view',
    'education.library.manage',
    'education.admin',
  ],
  '/education/library/titles/new': ['education.library.manage', 'education.admin'],
  '/education/library/reservations': [
    'education.library.view',
    'education.library.manage',
    'education.library.circulate',
    'education.admin',
  ],
  '/education/library/settings': [
    'education.library.view',
    'education.library.manage',
    'education.library.circulate',
    'education.admin',
  ],
  '/education/library/reservations/new': [
    'education.library.circulate',
    'education.admin',
  ],
  '/education/library/issue': ['education.library.circulate', 'education.admin'],
  '/education/library/loans': [
    'education.library.view',
    'education.library.circulate',
    'education.admin',
  ],
  '/education/assessments': [
    'education.assessment.view',
    'education.assessment.entry',
    'education.assessment.publish',
    'education.admin',
  ],
  '/education/assessments/schemes/new': ['education.assessment.entry', 'education.admin'],
  '/education/questions': ['education.question.view', 'education.question.manage', 'education.admin'],
  '/education/questions/items': ['education.question.view', 'education.question.manage', 'education.admin'],
  '/education/questions/items/new': ['education.question.manage', 'education.admin'],
  '/education/question-blueprints': ['education.question.view', 'education.question.manage', 'education.admin'],
  '/education/question-blueprints/new': ['education.question.manage', 'education.admin'],
  '/education/hpc': ['education.hpc.view', 'education.hpc.manage', 'education.admin'],
  '/education/hpc/templates/new': ['education.hpc.manage', 'education.admin'],
  '/education/hpc/stages/new': ['education.hpc.manage', 'education.admin'],
  '/education/lms': ['education.lms.view', 'education.lms.manage', 'education.admin'],
  '/education/lms/lesson-plans': ['education.lms.view', 'education.lms.manage', 'education.admin'],
  '/education/lms/lesson-plans/new': ['education.lms.manage', 'education.admin'],
  '/education/lms/homework': ['education.lms.view', 'education.lms.manage', 'education.admin'],
  '/education/lms/homework/new': ['education.lms.manage', 'education.admin'],
  '/education/lms/resources': ['education.lms.view', 'education.lms.manage', 'education.admin'],
  '/education/lms/resources/new': ['education.lms.manage', 'education.admin'],
  '/education/lms/announcements': ['education.lms.view', 'education.lms.manage', 'education.admin'],
  '/education/lms/announcements/new': ['education.lms.manage', 'education.admin'],
  '/education/activities': [
    'education.activity.view',
    'education.activity.manage',
    'education.admin',
  ],
  '/education/activities/list': [
    'education.activity.view',
    'education.activity.manage',
    'education.admin',
  ],
  '/education/activities/new': ['education.activity.manage', 'education.admin'],
  '/education/activities/memberships': [
    'education.activity.view',
    'education.activity.manage',
    'education.admin',
  ],
  '/education/activities/memberships/new': ['education.activity.manage', 'education.admin'],
  '/education/activities/memberships/bulk': ['education.activity.manage', 'education.admin'],
  '/education/activities/achievements': [
    'education.activity.view',
    'education.activity.manage',
    'education.admin',
  ],
  '/education/activities/achievements/new': ['education.activity.manage', 'education.admin'],
  '/education/activities/meetings': [
    'education.activity.view',
    'education.activity.manage',
    'education.admin',
  ],
  '/education/timetable': [
    'education.timetable.view',
    'education.timetable.manage',
    'education.admin',
  ],
  '/education/timetable/smart': [
    'education.timetable.smart.view',
    'education.timetable.smart.manage',
    'education.timetable.view',
    'education.timetable.manage',
    'education.admin',
  ],
  '/education/timetable/smart/setup': [
    'education.timetable.smart.manage',
    'education.timetable.manage',
    'education.admin',
  ],
  '/education/timetable/smart/scenarios': [
    'education.timetable.smart.view',
    'education.timetable.smart.manage',
    'education.timetable.view',
    'education.timetable.manage',
    'education.admin',
  ],
  '/education/timetable/periods': [
    'education.timetable.view',
    'education.timetable.manage',
    'education.admin',
  ],
  '/education/timetable/periods/new': ['education.timetable.manage', 'education.admin'],
  '/education/timetable/rooms': [
    'education.timetable.view',
    'education.timetable.manage',
    'education.admin',
  ],
  '/education/timetable/rooms/new': ['education.timetable.manage', 'education.admin'],
  '/education/timetable/grids': [
    'education.timetable.view',
    'education.timetable.manage',
    'education.admin',
  ],
  '/education/timetable/teacher': [
    'education.timetable.view',
    'education.timetable.manage',
    'education.admin',
  ],
  '/education/substitutions': [
    'education.substitution.view',
    'education.substitution.manage',
    'education.admin',
  ],
  '/education/substitutions/new': ['education.substitution.manage', 'education.admin'],
  '/education/curriculum': [
    'education.curriculum.view',
    'education.curriculum.manage',
    'education.admin',
  ],
  '/education/curriculum/units': [
    'education.curriculum.view',
    'education.curriculum.manage',
    'education.admin',
  ],
  '/education/curriculum/import': ['education.curriculum.manage', 'education.admin'],
  '/education/curriculum/units/new': ['education.curriculum.manage', 'education.admin'],
  '/education/workspace': [
    'education.timetable.view',
    'education.timetable.manage',
    'education.attendance.view',
    'education.attendance.mark',
    'education.attendance.manage',
    'education.substitution.view',
    'education.substitution.manage',
    'education.curriculum.view',
    'education.admin',
  ],
};

const COMPARTMENT_ROUTE_PERMISSIONS: Record<string, string[]> = {
  '/education/health': ['education.health.view', 'education.health.edit'],
  '/education/counseling': ['education.counseling.view', 'education.counseling.edit'],
  '/education/safeguarding': ['education.safeguarding.view', 'education.safeguarding.edit'],
};

function hasAnyPermission(userPermissions: string[], required: string[]): boolean {
  return required.some((code) => userPermissions.includes(code));
}

export function canAccessRouteByPermissions(
  rawPath: string,
  userPermissions: string[],
  isSuperAdmin: boolean
): boolean {
  const path = toCurrentRoutePath(rawPath);
  const compartmentRequired = COMPARTMENT_ROUTE_PERMISSIONS[path];
  if (compartmentRequired) {
    return hasAnyPermission(userPermissions, compartmentRequired);
  }
  if (path.startsWith('/education/attendance/corrections')) {
    return hasAnyPermission(userPermissions, ['education.attendance.correct', 'education.attendance.view']);
  }
  if (path.startsWith('/education/attendance/leave')) {
    return hasAnyPermission(userPermissions, ['education.leave.manage', 'education.leave.review', 'education.attendance.view']);
  }
  if (path.startsWith('/education/attendance/settings')) {
    return hasAnyPermission(userPermissions, ['education.attendance.manage']);
  }
  if (path.startsWith('/education/attendance')) {
    return hasAnyPermission(userPermissions, ['education.attendance.view', 'education.attendance.mark', 'education.attendance.manage', 'education.attendance.correct']);
  }
  if (path.startsWith('/education/students/') && path.endsWith('/edit')) {
    return hasAnyPermission(userPermissions, ['education.student.manage']);
  }
  if (path.startsWith('/education/students/') && path.endsWith('/attendance')) {
    return hasAnyPermission(userPermissions, ['education.attendance.view', 'education.attendance.mark']);
  }
  if (path.startsWith('/education/students/') && path.endsWith('/twin')) {
    return hasAnyPermission(userPermissions, ['education.student_twin.view', 'education.admin']);
  }
  if (path === '/education/enrollments/new') {
    return hasAnyPermission(userPermissions, ['education.enrollment.manage']);
  }
  if (path.startsWith('/education/enrollments/') && (path.includes('/allocate') || path.includes('/transfer'))) {
    return hasAnyPermission(userPermissions, ['education.section.allocate']);
  }
  if (path.startsWith('/education/enrollments/') && path.includes('/withdrawal')) {
    return hasAnyPermission(userPermissions, ['education.withdrawal.manage']);
  }
  if (path.startsWith('/education/enrollments')) {
    return hasAnyPermission(userPermissions, ['education.enrollment.view', 'education.enrollment.manage', 'education.enrollment.approve', 'education.section.allocate', 'education.withdrawal.manage']);
  }
  if (path.startsWith('/education/progression')) {
    return hasAnyPermission(userPermissions, ['education.progression.manage', 'education.enrollment.view']);
  }
  if (path.startsWith('/education/transfer-certificates')) {
    return hasAnyPermission(userPermissions, ['education.transfer_certificate.issue', 'education.enrollment.view']);
  }
  if (path.startsWith('/education/students/') && path.endsWith('/academic-history')) {
    return hasAnyPermission(userPermissions, ['education.enrollment.view', 'education.enrollment.manage']);
  }
  if (path === '/education/admissions/new' || path === '/education/admissions/enquiries/new') {
    return hasAnyPermission(userPermissions, ['education.admission.manage']);
  }
  if (path === '/education/admissions' || path.startsWith('/education/admissions/')) {
    return hasAnyPermission(userPermissions, [
      'education.admission.view',
      'education.admission.manage',
      'education.admission.decide',
    ]);
  }
  if (path === '/education/academic/sessions/new') {
    return hasAnyPermission(userPermissions, ['education.academic.manage']);
  }
  if (path.startsWith('/education/transport')) {
    return hasAnyPermission(userPermissions, [
      'education.transport.view',
      'education.transport.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/library')) {
    return hasAnyPermission(userPermissions, [
      'education.library.view',
      'education.library.manage',
      'education.library.circulate',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/assessments/schemes/') && path.endsWith('/publish')) {
    return hasAnyPermission(userPermissions, ['education.assessment.publish', 'education.admin']);
  }
  if (path.startsWith('/education/assessments/schemes/') && path.endsWith('/entry')) {
    return hasAnyPermission(userPermissions, ['education.assessment.entry', 'education.admin']);
  }
  if (path.startsWith('/education/assessments')) {
    return hasAnyPermission(userPermissions, [
      'education.assessment.view',
      'education.assessment.entry',
      'education.assessment.publish',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/questions/items/') || path.startsWith('/education/question-blueprints/new')) {
    return hasAnyPermission(userPermissions, ['education.question.manage', 'education.admin']);
  }
  if (path.startsWith('/education/questions') || path.startsWith('/education/question-blueprints')) {
    return hasAnyPermission(userPermissions, [
      'education.question.view',
      'education.question.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/lms/homework/') && path.endsWith('/submit')) {
    return hasAnyPermission(userPermissions, ['education.lms.submit']);
  }
  if (path.startsWith('/education/lms') && (path.endsWith('/new') || path.endsWith('/edit'))) {
    return hasAnyPermission(userPermissions, ['education.lms.manage', 'education.admin']);
  }
  if (path.startsWith('/education/lms')) {
    return hasAnyPermission(userPermissions, ['education.lms.view', 'education.lms.manage', 'education.admin']);
  }
  if (path.startsWith('/education/hpc/templates/') || path === '/education/hpc/stages/new') {
    return hasAnyPermission(userPermissions, ['education.hpc.manage', 'education.admin']);
  }
  if (path.startsWith('/education/hpc')) {
    return hasAnyPermission(userPermissions, ['education.hpc.view', 'education.hpc.manage', 'education.admin']);
  }
  if (path.startsWith('/education/activities')) {
    return hasAnyPermission(userPermissions, [
      'education.activity.view',
      'education.activity.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/timetable')) {
    return hasAnyPermission(userPermissions, [
      'education.timetable.view',
      'education.timetable.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/substitutions')) {
    return hasAnyPermission(userPermissions, [
      'education.substitution.view',
      'education.substitution.manage',
      'education.admin',
    ]);
  }
  if (path === '/education/curriculum/import') {
    return hasAnyPermission(userPermissions, ['education.curriculum.manage', 'education.admin']);
  }
  if (path.startsWith('/education/curriculum')) {
    return hasAnyPermission(userPermissions, [
      'education.curriculum.view',
      'education.curriculum.manage',
      'education.admin',
    ]);
  }
  if (path === '/education/workspace' || path.startsWith('/education/workspace/')) {
    return hasAnyPermission(userPermissions, [
      'education.timetable.view',
      'education.timetable.manage',
      'education.attendance.view',
      'education.attendance.mark',
      'education.attendance.manage',
      'education.substitution.view',
      'education.substitution.manage',
      'education.curriculum.view',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/behaviour')) {
    return hasAnyPermission(userPermissions, [
      'education.behaviour.view',
      'education.behaviour.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/inclusion')) {
    return hasAnyPermission(userPermissions, [
      'education.inclusion.view',
      'education.inclusion.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/intervention')) {
    return hasAnyPermission(userPermissions, [
      'education.intervention.view',
      'education.intervention.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/standards')) {
    return hasAnyPermission(userPermissions, [
      'education.standards.view',
      'education.standards.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/governance')) {
    return hasAnyPermission(userPermissions, [
      'education.governance.view',
      'education.governance.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/app')) {
    return hasAnyPermission(userPermissions, [
      'education.app.view',
      'education.app.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/eqams')) {
    return hasAnyPermission(userPermissions, [
      'education.eqams.view',
      'education.eqams.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/portal/parent')) {
    return hasAnyPermission(userPermissions, ['education.portal.parent', 'education.admin']);
  }
  if (path.startsWith('/education/portal/student')) {
    return hasAnyPermission(userPermissions, ['education.portal.student', 'education.admin']);
  }
  if (path.startsWith('/education/portal/teacher')) {
    return hasAnyPermission(userPermissions, [
      'education.portal.teacher',
      'education.timetable.view',
      'education.attendance.view',
      'education.attendance.mark',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/udise')) {
    return hasAnyPermission(userPermissions, [
      'education.udise.view',
      'education.udise.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/ids')) {
    return hasAnyPermission(userPermissions, [
      'education.ids.view',
      'education.ids.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/comms')) {
    return hasAnyPermission(userPermissions, [
      'education.comms.view',
      'education.comms.manage',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/print')) {
    return hasAnyPermission(userPermissions, [
      'education.transfer_certificate.issue',
      'education.enrollment.view',
      'education.fee.view',
      'education.fee.collect',
      'education.fee.manage',
      'education.student.view',
      'education.student.view_all',
      'education.attendance.view',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/pulse')) {
    return hasAnyPermission(userPermissions, ['education.pulse.view', 'education.admin']);
  }
  if (path.startsWith('/education/quality-copilot')) {
    return hasAnyPermission(userPermissions, ['education.quality_copilot.view', 'education.admin']);
  }
  if (path.startsWith('/education/process-insights')) {
    return hasAnyPermission(userPermissions, ['education.process_insights.view', 'education.admin']);
  }
  if (path.startsWith('/education/reports')) {
    return hasAnyPermission(userPermissions, [
      'education.reports.view',
      'education.fee.view',
      'education.attendance.view',
      'education.academic.view',
      'education.admin',
    ]);
  }
  if (path.startsWith('/education/fees')) {
    return hasAnyPermission(userPermissions, [
      'education.fee.view',
      'education.fee.manage',
      'education.fee.demand',
      'education.fee.collect',
      'education.fee.concession',
      'education.fee.promise',
      'education.fee.dispute',
      'education.admin',
    ]);
  }
  if (path === '/education/policy' || path.startsWith('/education/academic')) {
    return hasAnyPermission(userPermissions, [
      'education.academic.view',
      'education.academic.manage',
      'education.policy.publish',
    ]);
  }
  if (isSuperAdmin) return true;
  if (/^\/growth\/estimations\/assembly\/[^/]+$/.test(path)) {
    return hasAnyPermission(userPermissions, [
      'growth.assembly_estimation.view',
      'growth.assembly_estimation.create',
      'growth.assembly_estimation.edit',
    ]);
  }
  let required = ROUTE_REQUIRED_PERMISSIONS[path];
  if ((!required || required.length === 0) && /^\/growth\/demos\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/growth/demos'];
  }
  if ((!required || required.length === 0) && /^\/growth\/activities\/[^/]+\/edit$/.test(path)) {
    required = ['crm.activity.edit'];
  }
  if ((!required || required.length === 0) && /^\/grc\/risk-assessments\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/grc/risk-assessments'];
  }
  if ((!required || required.length === 0) && /^\/platform\/workflows\/approvals\/[^/]+\/edit$/.test(path)) {
    required = [
      'settings.workflows.view',
      'settings.workflows.create',
      'settings.workflows.edit',
      'settings.workflows.delete',
    ];
  }
  if ((!required || required.length === 0) && /^\/prj\/projects\/[^/]+\/edit$/.test(path)) {
    required = ['project.edit'];
  }
  if ((!required || required.length === 0) && /^\/mfg\/work-orders\/[^/]+\/print$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/mfg/work-orders'];
  }
  if ((!required || required.length === 0) && /^\/mfg\/inland-process-deviations\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/mfg/work-orders'];
  }
  if ((!required || required.length === 0) && /^\/mfg\/operations\/[^/]+\/edit$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/mfg/operations'];
  }
  if ((!required || required.length === 0) && /^\/mfg\/boms\/(?!new$)[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/mfg/boms'];
  }
  if ((!required || required.length === 0) && /^\/hr\/employees\/[^/]+\/edit$/.test(path)) {
    required = ['hr.employee.edit'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/hr\/employees\/[^/]+$/.test(path) &&
    path !== '/hr/employees/new' &&
    !path.endsWith('/edit')
  ) {
    required = ['hr.view', 'hr.employee.create', 'hr.employee.edit', 'hr.employee.delete', 'company.view', 'user.view'];
  }
  if ((!required || required.length === 0) && /^\/fin\/tax\/vat-return\/.+/.test(path)) {
    required = ['finance.vatreturn.view', 'finance.view'];
  }
  if ((!required || required.length === 0) && path.startsWith('/fin/prepaid/')) {
    if (/^\/fin\/prepaid\/contracts\/[^/]+\/edit$/.test(path)) {
      required = ['finance.recognition.edit'];
    } else if (/^\/fin\/prepaid\/contracts\/[^/]+\/adjust$/.test(path)) {
      required = [
        'finance.recognition.revise',
        'finance.recognition.suspend',
        'finance.recognition.resume',
        'finance.recognition.terminate',
        'finance.recognition.refund',
        'finance.recognition.writeoff',
        'finance.recognition.reverse',
      ];
    } else if (/^\/fin\/prepaid\/runs\/[^/]+$/.test(path)) {
      required = ['finance.recognition.view', 'finance.recognition.run', 'finance.recognition.post'];
    } else {
      required = ['finance.recognition.view'];
    }
  }
  if (
    (!required || required.length === 0) &&
    /^\/growth\/orders\/[^/]+\/create-work-order$/.test(path)
  ) {
    required = ['growth.order.view', 'growth.view'];
  }
  if ((!required || required.length === 0) && /^\/growth\/orders\/[^/]+\/timesheets$/.test(path)) {
    required = ['growth.order_timesheet.view', 'growth.order.view', 'growth.view'];
  }
  if ((!required || required.length === 0) && /^\/growth\/orders\/[^/]+\/status-check$/.test(path)) {
    required = ['growth.order.view', 'growth.view'];
  }
  if ((!required || required.length === 0) && /^\/prj\/projects\/[^/]+\/status-check$/.test(path)) {
    required = ['project.view'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/growth\/inland-process-deviations\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/growth/inland-process-deviations'];
  }
  if ((!required || required.length === 0) && /^\/growth\/leads\/[^/]+\/edit$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/growth/leads'];
  }
  if ((!required || required.length === 0) && /^\/qms\/dimensional-inspection\/[^/]+\/edit$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/dimensional-inspection'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/qms\/receiving-inspections\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/receiving-inspections'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/qms\/receiving-dimensional-inspections\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/receiving-dimensional-inspections'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/procurement\/receiving-inspections\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/procurement/receiving-inspections'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/procurement\/receiving-dimensional-inspections\/[^/]+$/.test(path)
  ) {
    required =
      ROUTE_REQUIRED_PERMISSIONS['/scm/procurement/receiving-dimensional-inspections'];
  }
  if ((!required || required.length === 0) && /^\/growth\/opportunities\/[^/]+\/edit$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/growth/opportunities'];
  }
  if ((!required || required.length === 0) && /^\/growth\/leads\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/growth/leads'];
  }
  if ((!required || required.length === 0) && /^\/growth\/opportunities\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/growth/opportunities'];
  }
  if ((!required || required.length === 0) && /^\/growth\/customers\/[^/]+\/360$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/growth/customers'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/quality\/supplier-assessments\/(?!new$)[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/quality/supplier-assessments'];
  }
  if ((!required || required.length === 0) && /^\/scm\/procurement\/suppliers\/[^/]+\/360$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/procurement/suppliers'];
  }
  if ((!required || required.length === 0) && /^\/scm\/procurement\/suppliers\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/procurement/suppliers'];
  }
  if ((!required || required.length === 0) && /^\/platform\/foundation\/customers\/[^/]+\/360$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/platform/foundation/customers'];
  }
  if ((!required || required.length === 0) && /^\/platform\/foundation\/suppliers\/[^/]+\/360$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/platform/foundation/suppliers'];
  }
  if ((!required || required.length === 0) && /^\/platform\/foundation\/suppliers\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/platform/foundation/suppliers'];
  }
  if ((!required || required.length === 0) && /^\/platform\/companies\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/platform/companies'];
  }
  if ((!required || required.length === 0) && /^\/growth\/document-collections\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/growth/document-collections'];
  }
  if ((!required || required.length === 0) && /^\/growth\/delivery-notes\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/growth/delivery-notes'];
  }
  if ((!required || required.length === 0) && /^\/print\/sales\/delivery-notes\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/growth/delivery-notes'];
  }
  if ((!required || required.length === 0) && /^\/qms\/final-inspection-reports\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/final-inspection-reports'];
  }
  if ((!required || required.length === 0) && /^\/print\/qms\/final-inspection-reports\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/final-inspection-reports'];
  }
  if ((!required || required.length === 0) && /^\/qms\/inspection-release-notes\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/inspection-release-notes'];
  }
  if ((!required || required.length === 0) && /^\/print\/qms\/inspection-release-notes\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/inspection-release-notes'];
  }
  if ((!required || required.length === 0) && /^\/qms\/certificates-of-conformity\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/certificates-of-conformity'];
  }
  if ((!required || required.length === 0) && /^\/qms\/equipments\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/equipments'];
  }
  if ((!required || required.length === 0) && /^\/qms\/tmmde\/impact\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/tmmde/impact/:id'];
  }
  if ((!required || required.length === 0) && /^\/qms\/tmmde\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/tmmde'];
  }
  if ((!required || required.length === 0) && /^\/grc\/calibration\/schedules\/[^/]+\/edit$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/grc/calibration/schedules/:id/edit'];
  }
  if ((!required || required.length === 0) && /^\/grc\/calibration\/records\/[^/]+\/edit$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/grc/calibration/records/:id/edit'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/grc\/calibration\/records\/[^/]+$/.test(path) &&
    path !== '/grc/calibration/records/new'
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/grc/calibration/records/:id'];
  }
  if ((!required || required.length === 0) && /^\/scm\/warehouse\/stock-adjustment\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/warehouse/stock-adjustment'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/inventory\/stock-count\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/inventory/stock-count'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/warehouse\/warehouses\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/warehouse/warehouses/:id'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/inventory\/consignment-receipts\/[^/]+$/.test(path) &&
    path !== '/scm/inventory/consignment-receipts/new'
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/inventory/consignment-receipts'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/inventory\/consignment-returns\/[^/]+$/.test(path) &&
    path !== '/scm/inventory/consignment-returns/new'
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/inventory/consignment-returns'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/warehouse\/stock-transfer\/[^/]+$/.test(path) &&
    path !== '/scm/warehouse/stock-transfer/new'
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/warehouse/stock-transfer'];
  }
  if ((!required || required.length === 0) && /^\/epc\/commercial\/tenders\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/tenders'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/tenders\/[^/]+\/bid$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/tenders'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/estimate-revision\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/tenders'];
  }
  if ((!required || required.length === 0) && /^\/epc\/commercial\/prequalifications\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/prequalifications'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/prequalifications\/[^/]+\/continue$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/prequalifications'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/pursuits\/resolve\/[^/]+\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/pursuits/resolve/:entityType/:entityId'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/pursuits\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/pursuits/:opportunityId'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/contracts\/[^/]+\/control$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/contracts/:id/control'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/contracts\/[^/]+\/payment-applications\/new$/.test(path)
  ) {
    required =
      ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/contracts/:id/payment-applications/new'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/contracts\/[^/]+\/payment-applications\/[^/]+$/.test(path)
  ) {
    required =
      ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/contracts/:id/payment-applications/:paId'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/contracts\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/contracts/:id'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/subcontracts\/[^/]+\/control$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/subcontracts/:id/control'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/subcontracts\/[^/]+\/certificates\/new$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/subcontracts/:id/certificates/new'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/subcontracts\/[^/]+\/certificates\/[^/]+$/.test(path)
  ) {
    required =
      ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/subcontracts/:id/certificates/:spcId'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/subcontracts\/[^/]+\/edit$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/subcontracts/:id/edit'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/epc\/commercial\/subcontracts\/[^/]+$/.test(path) &&
    path !== '/epc/commercial/subcontracts/new'
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/epc/commercial/subcontracts/:id'];
  }
  if ((!required || required.length === 0) && /^\/mfg\/work-orders\/[^/]+\/route-sheet$/.test(path)) {
    required = ['manufacturing.route_sheet.view', 'manufacturing.view'];
  }
  if ((!required || required.length === 0) && /^\/mfg\/work-orders\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/mfg/work-orders'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/inventory\/material-requests\/[^/]+\/edit$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/inventory/material-requests/:id/edit'];
  }
  if ((!required || required.length === 0) && /^\/scm\/inventory\/material-requests\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/inventory/material-requests'];
  }
  if ((!required || required.length === 0) && /^\/scm\/inventory\/material-issues\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/scm/inventory/material-issues'];
  }
  if ((!required || required.length === 0) && /^\/qms\/initial-inspection-requests\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/qms/initial-inspection-requests'];
  }
  if ((!required || required.length === 0) && /^\/growth\/quotations\/[^/]+\/source-sync$/.test(path)) {
    required = ['crm.quotation.view', 'crm.quotation.edit', 'growth.view'];
  }
  if ((!required || required.length === 0) && /^\/mfg\/production-requests\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/mfg/production-requests'];
  }
  if ((!required || required.length === 0) && /^\/asset\/assets\/[^/]+\/edit$/.test(path)) {
    required = ['asset.edit'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/tasks\/module\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/home/team-tasks'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/home\/tasks\/[^/]+$/.test(path) &&
    path !== '/home/tasks'
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/home/tasks'];
  }
  if ((!required || required.length === 0) && path === '/scm/procurement/purchase-requests/new') {
    required = ['procurement.pr.create', 'procurement.po.create'];
  }
  if ((!required || required.length === 0) && /^\/finance\/ap\/supplier-advance-refunds\/[^/]+\/edit$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/finance/ap/supplier-advance-refunds'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/finance\/ap\/supplier-advance-refunds\/[^/]+$/.test(path) &&
    path !== '/finance/ap/supplier-advance-refunds/new'
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/finance/ap/supplier-advance-refunds'];
  }
  if ((!required || required.length === 0) && path === '/finance/ap/supplier-advance-refunds/new') {
    required = ROUTE_REQUIRED_PERMISSIONS['/finance/ap/supplier-advance-refunds'];
  }
  if ((!required || required.length === 0) && /^\/scm\/procurement\/purchase-requests\/[^/]+\/edit$/.test(path)) {
    required = ['procurement.pr.edit', 'procurement.po.edit'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/procurement\/purchase-requests\/(?!new$)[^/]+$/.test(path)
  ) {
    required = ['procurement.pr.view', 'procurement.po.view', 'procurement.view'];
  }
  if ((!required || required.length === 0) && path === '/scm/procurement/supplier-inquiries/new') {
    required = ['procurement.rfq.create', 'procurement.po.create'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/procurement\/supplier-inquiries\/(?!new$)[^/]+$/.test(path)
  ) {
    required = ['procurement.rfq.view', 'procurement.po.view', 'procurement.view'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/procurement\/supplier-quotations\/[^/]+$/.test(path)
  ) {
    required = ['procurement.rfq.view', 'procurement.po.view', 'procurement.view'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/procurement\/bid-analyses\/[^/]+$/.test(path)
  ) {
    required = ['procurement.rfq.view', 'procurement.po.view', 'procurement.view'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/procurement\/grn\/[^/]+\/edit$/.test(path)
  ) {
    required = ['procurement.grn.edit', 'procurement.po.edit'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/scm\/procurement\/grn\/(?!new$)[^/]+$/.test(path)
  ) {
    required = ['procurement.grn.view', 'procurement.po.view', 'procurement.view'];
  }
  if (
    (!required || required.length === 0) &&
    /^\/grc\/moc\/(?!new$)[^/]+$/.test(path)
  ) {
    required = ['moc.view', 'company.view'];
  }
  if ((!required || required.length === 0) && /^\/grc\/competency\/job-profiles\/[^/]+$/.test(path)) {
    required = COMPETENCY_VIEW_ANY;
  }
  if ((!required || required.length === 0) && /^\/grc\/competency\/sessions\/[^/]+$/.test(path)) {
    required = COMPETENCY_TRAINING_VIEW;
  }
  if ((!required || required.length === 0) && path.startsWith('/cspm/')) {
    if (path === '/cspm/requirements/new') {
      required = ['cspm.requirement.create', 'cspm.view'];
    } else if (path === '/cspm/receipts/new') {
      required = ['cspm.receipt.create', 'cspm.view'];
    } else if (path === '/cspm/returns/new') {
      required = ['cspm.return_customer', 'cspm.view'];
    } else if (path === '/cspm/reconciliations' || /^\/cspm\/reconciliations\/[^/]+$/.test(path)) {
      required = ['cspm.reconcile', 'cspm.view'];
    } else {
      required = ['cspm.view'];
    }
  }
  if (
    (!required || required.length === 0) &&
    /^\/hr\/me\/timesheets\/[^/]+$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/hr/me/timesheets'];
  }
  if ((!required || required.length === 0) && path.startsWith('/hr/')) {
    const recruitRoot = path.match(/^\/hr\/(requisitions|jobs|candidates|applications|interviews|assessments|offers|onboarding)\/[^/]+(\/[^/]+)?$/);
    if (recruitRoot) {
      const [, entity, suffix] = recruitRoot;
      if (entity === 'interviews' && suffix === '/scorecard') {
        required = ['hr.recruit.interview.feedback', 'hr.recruit.manage'];
      } else if (entity === 'interviews' && !suffix) {
        required = [...ROUTE_REQUIRED_PERMISSIONS['/hr/interviews'], 'hr.recruit.interview.feedback'];
      } else if (entity === 'offers' && suffix === '/convert') {
        required = ['hr.recruit.convert'];
      } else if (suffix === '/edit') {
        required = ['hr.recruit.manage'];
      } else {
        required = ROUTE_REQUIRED_PERMISSIONS[`/hr/${entity}`];
      }
    }
  }
  if ((!required || required.length === 0) && /^\/help\/[^/]+$/.test(path)) {
    required = ROUTE_REQUIRED_PERMISSIONS['/help'];
  }
  if (!required || required.length === 0) {
    if (path.startsWith('/mfg/')) {
      required = [
        'manufacturing.view',
        'manufacturing.reports.view',
        'manufacturing.bom.manage',
        'manufacturing.wo.create',
        'manufacturing.control_tower.view',
        'manufacturing.workstation.view',
      ];
    } else if (path.startsWith('/qms/') || path.startsWith('/quality/')) {
      required = [
        'manufacturing.view',
        'quality.plan.view',
        'quality.inspection.view',
        'quality.stage_inspection.view',
        'quality.stage_inspection.complete',
        'quality.initial_inspection.view',
        'quality.final_inspection.view',
        'quality.dimensional_inspection.view',
        'quality.inspection_release_note.view',
        'quality.certificate_of_conformity.view',
        'quality.ncr.view',
        'quality.capa.view',
        'quality.kpi.view',
        'quality.management_review.view',
        'quality.document_control.view',
      ];
    } else if (path.startsWith('/growth/')) {
      required = ['growth.view'];
    } else if (path.startsWith('/scm/')) {
      required = ['procurement.view', 'inventory.view'];
    } else if (path.startsWith('/prj/')) {
      required = ['project.view', 'project.reports.view', 'project.costing.view'];
    } else if (path.startsWith('/education/attendance/corrections')) {
      required = ['education.attendance.correct', 'education.attendance.view'];
    } else if (path.startsWith('/education/attendance/leave')) {
      required = ['education.leave.manage', 'education.leave.review', 'education.attendance.view'];
    } else if (path.startsWith('/education/attendance/settings')) {
      required = ['education.attendance.manage'];
    } else if (path === '/education/attendance' || path.startsWith('/education/attendance/')) {
      required = ['education.attendance.view', 'education.attendance.mark', 'education.attendance.manage', 'education.attendance.correct'];
    } else if (path.startsWith('/education/students/') && path.endsWith('/edit')) {
      required = ['education.student.manage'];
    } else if (path.startsWith('/education/students/') && path.endsWith('/attendance')) {
      required = ['education.attendance.view', 'education.attendance.mark'];
    } else if (path.startsWith('/education/students/') && path.endsWith('/twin')) {
      required = ['education.student_twin.view', 'education.admin'];
    } else if (path === '/education/enrollments/new') {
      required = ['education.enrollment.manage'];
    } else if (path.startsWith('/education/enrollments/') && (path.includes('/allocate') || path.includes('/transfer'))) {
      required = ['education.section.allocate'];
    } else if (path.startsWith('/education/enrollments/') && path.includes('/withdrawal')) {
      required = ['education.withdrawal.manage'];
    } else if (path === '/education/enrollments' || path.startsWith('/education/enrollments/')) {
      required = ['education.enrollment.view', 'education.enrollment.manage', 'education.enrollment.approve', 'education.section.allocate', 'education.withdrawal.manage'];
    } else if (path === '/education/progression' || path.startsWith('/education/progression/')) {
      required = ['education.progression.manage', 'education.enrollment.view'];
    } else if (path === '/education/transfer-certificates' || path.startsWith('/education/transfer-certificates/')) {
      required = ['education.transfer_certificate.issue', 'education.enrollment.view'];
    } else if (path.startsWith('/education/students/') && path.endsWith('/academic-history')) {
      required = ['education.enrollment.view', 'education.enrollment.manage'];
    } else if (path === '/education/admissions/new' || path === '/education/admissions/enquiries/new') {
      required = ['education.admission.manage'];
    } else if (path === '/education/admissions' || path.startsWith('/education/admissions/')) {
      required = ['education.admission.view', 'education.admission.manage', 'education.admission.decide'];
    } else if (path === '/education/academic/sessions/new') {
      required = ['education.academic.manage'];
    } else if (path === '/education/academic' || path.startsWith('/education/academic/')) {
      required = ['education.academic.view', 'education.academic.manage', 'education.policy.publish'];
    } else if (path === '/education/policy' || path.startsWith('/education/policy/')) {
      required = ['education.academic.view', 'education.academic.manage', 'education.policy.publish'];
    } else if (path === '/education/students/new') {
      required = ['education.student.manage'];
    } else if (path.startsWith('/education/transport')) {
      required = ['education.transport.view', 'education.transport.manage', 'education.admin'];
    } else if (path.startsWith('/education/library')) {
      required = [
        'education.library.view',
        'education.library.manage',
        'education.library.circulate',
        'education.admin',
      ];
    } else if (path.startsWith('/education/assessments/schemes/') && path.endsWith('/publish')) {
      required = ['education.assessment.publish', 'education.admin'];
    } else if (path.startsWith('/education/assessments/schemes/') && path.endsWith('/entry')) {
      required = ['education.assessment.entry', 'education.admin'];
    } else if (path.startsWith('/education/assessments')) {
      required = [
        'education.assessment.view',
        'education.assessment.entry',
        'education.assessment.publish',
        'education.admin',
      ];
    } else if (path.startsWith('/education/questions/items/') || path.startsWith('/education/question-blueprints/new')) {
      required = ['education.question.manage', 'education.admin'];
    } else if (path.startsWith('/education/questions') || path.startsWith('/education/question-blueprints')) {
      required = ['education.question.view', 'education.question.manage', 'education.admin'];
    } else if (path.startsWith('/education/lms/homework/') && path.endsWith('/submit')) {
      required = ['education.lms.submit'];
    } else if (path.startsWith('/education/lms') && (path.endsWith('/new') || path.endsWith('/edit'))) {
      required = ['education.lms.manage', 'education.admin'];
    } else if (path.startsWith('/education/lms')) {
      required = ['education.lms.view', 'education.lms.manage', 'education.admin'];
    } else if (path.startsWith('/education/activities')) {
      required = ['education.activity.view', 'education.activity.manage', 'education.admin'];
    } else if (path.startsWith('/education/timetable')) {
      required = ['education.timetable.view', 'education.timetable.manage', 'education.admin'];
    } else if (path.startsWith('/education/substitutions')) {
      required = ['education.substitution.view', 'education.substitution.manage', 'education.admin'];
    } else if (path.startsWith('/education/curriculum')) {
      required = ['education.curriculum.view', 'education.curriculum.manage', 'education.admin'];
    } else if (path === '/education/workspace' || path.startsWith('/education/workspace/')) {
      required = [
        'education.timetable.view',
        'education.timetable.manage',
        'education.attendance.view',
        'education.attendance.mark',
        'education.attendance.manage',
        'education.substitution.view',
        'education.substitution.manage',
        'education.curriculum.view',
        'education.admin',
      ];
    } else if (path.startsWith('/education/behaviour')) {
      required = ['education.behaviour.view', 'education.behaviour.manage', 'education.admin'];
    } else if (path.startsWith('/education/inclusion')) {
      required = ['education.inclusion.view', 'education.inclusion.manage', 'education.admin'];
    } else if (path.startsWith('/education/intervention')) {
      required = ['education.intervention.view', 'education.intervention.manage', 'education.admin'];
    } else if (path.startsWith('/education/standards')) {
      required = ['education.standards.view', 'education.standards.manage', 'education.admin'];
    } else if (path.startsWith('/education/governance')) {
      required = ['education.governance.view', 'education.governance.manage', 'education.admin'];
    } else if (path.startsWith('/education/app')) {
      required = ['education.app.view', 'education.app.manage', 'education.admin'];
    } else if (path.startsWith('/education/eqams')) {
      required = ['education.eqams.view', 'education.eqams.manage', 'education.admin'];
    } else if (path.startsWith('/education/portal/parent')) {
      required = ['education.portal.parent', 'education.admin'];
    } else if (path.startsWith('/education/portal/student')) {
      required = ['education.portal.student', 'education.admin'];
    } else if (path.startsWith('/education/portal/teacher')) {
      required = [
        'education.portal.teacher',
        'education.timetable.view',
        'education.attendance.view',
        'education.attendance.mark',
        'education.admin',
      ];
    } else if (path.startsWith('/education/udise')) {
      required = ['education.udise.view', 'education.udise.manage', 'education.admin'];
    } else if (path.startsWith('/education/ids')) {
      required = ['education.ids.view', 'education.ids.manage', 'education.admin'];
    } else if (path.startsWith('/education/comms')) {
      required = ['education.comms.view', 'education.comms.manage', 'education.admin'];
    } else if (path.startsWith('/education/print')) {
      required = [
        'education.transfer_certificate.issue',
        'education.enrollment.view',
        'education.fee.view',
        'education.fee.collect',
        'education.fee.manage',
        'education.student.view',
        'education.student.view_all',
        'education.attendance.view',
        'education.admin',
      ];
    } else if (path.startsWith('/education/pulse')) {
      required = ['education.pulse.view', 'education.admin'];
    } else if (path.startsWith('/education/quality-copilot')) {
      required = ['education.quality_copilot.view', 'education.admin'];
    } else if (path.startsWith('/education/process-insights')) {
      required = ['education.process_insights.view', 'education.admin'];
    } else if (path.startsWith('/education/reports')) {
      required = [
        'education.reports.view',
        'education.fee.view',
        'education.attendance.view',
        'education.academic.view',
        'education.admin',
      ];
    } else if (path.startsWith('/education/fees')) {
      required = [
        'education.fee.view',
        'education.fee.manage',
        'education.fee.demand',
        'education.fee.collect',
        'education.fee.concession',
        'education.fee.promise',
        'education.fee.dispute',
        'education.admin',
      ];
    } else if (path === '/education/students' || path.startsWith('/education/students/')) {
      required = [
        'education.student.view',
        'education.student.view_all',
        'education.student.manage',
        'education.guardian.manage',
        'education.staff.manage',
      ];
    } else if (path === '/education/settings' || path === '/education' || path.startsWith('/education/')) {
      required = ['education.access', 'education.admin'];
    } else if (path.startsWith('/epc/')) {
      required = [
        'epc.view',
        'epc.admin',
        'epc.execution',
        'epc.execution.manage',
        'epc.tender.view',
        'epc.tender.manage',
        'epc.commercial.dashboard',
        'epc.prequalification.view',
        'epc.contract.view',
        'project.view',
      ];
    } else if (path.startsWith('/hr/')) {
      required = ['hr.view'];
    } else if (path.startsWith('/fin/')) {
      required = ['finance.view'];
    } else if (path.startsWith('/owner-intelligence')) {
      required = ['owner_intelligence.view'];
    } else if (path.startsWith('/asset/')) {
      required = ['asset.view'];
    }
  }
  if (
    (!required || required.length === 0) &&
    /^\/print\/growth\/customer-satisfaction(\/[^/]+)?$/.test(path)
  ) {
    required = ROUTE_REQUIRED_PERMISSIONS['/growth/customer-satisfaction'];
  }
  if (!required || required.length === 0) return true;
  return hasAnyPermission(userPermissions, required);
}
